import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Plus, Pencil, Trash2, ChevronRight, ChevronDown, ChevronUp, Search, Package, FolderOpen, GripVertical, Table2, Variable, ArrowLeft, Copy, CopyPlus, Send, Check, CheckCircle, Clock, XCircle, Archive, Building2, FileDown, Loader2, Eye, EyeOff, Link2, Mail, History, RefreshCw } from 'lucide-react';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/Toast';
import Modal from '../../../components/Modal';
import ConfirmDialog from '../../../components/ConfirmDialog';
import api from '../../../services/api';
import MiniSpreadsheet from '../../pricelists/components/MiniSpreadsheet';
import OfferContentEditor from '../components/OfferContentEditor';
import { useTranslation } from 'react-i18next';

// ==================== HELPERS ====================

function buildTree(flatItems) {
  const map = {}; const roots = [];
  flatItems.forEach(item => { map[item.id] = { ...item, children: [] }; });
  flatItems.forEach(item => {
    if (item.parent_id && map[item.parent_id]) map[item.parent_id].children.push(map[item.id]);
    else roots.push(map[item.id]);
  });
  const sort = (nodes) => { nodes.sort((a, b) => a.display_order - b.display_order); nodes.forEach(n => sort(n.children)); };
  sort(roots);
  return roots;
}

const STATUS = {
  draft: { labelKey: 'offers.statusDraft', color: 'bg-gray-100 text-gray-600', icon: Clock },
  sent: { labelKey: 'offers.statusSent', color: 'bg-blue-100 text-blue-700', icon: Send },
  accepted: { labelKey: 'offers.statusAccepted', color: 'bg-green-100 text-green-700', icon: Check },
  rejected: { labelKey: 'offers.statusRejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  archived: { labelKey: 'offers.statusArchived', color: 'bg-gray-100 text-gray-500', icon: Archive },
};

// ==================== OFFERS LIST ====================

function OffersList({ onSelect, canCreate, canDelete, toast, onAction }) {
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    try { const r = await api.get('/offers'); setOffers(r.data.offers); }
    catch { toast.error(t('common.error')); } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    try {
      const r = await api.post('/offers', data);
      toast.success(t('common.created'));
      setShowForm(false); load();
      onSelect(r.data.offer);
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/offers/${deleteTarget.id}`); toast.success(t('common.deleted')); setDeleteTarget(null); load(); }
    catch { toast.error(t('common.error')); }
  };

  const filtered = offers.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    return o.name.toLowerCase().includes(search.toLowerCase()) ||
           o.offer_number.toLowerCase().includes(search.toLowerCase()) ||
           (o.client_name && o.client_name.toLowerCase().includes(search.toLowerCase()));
  });

  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FileText size={28} className="text-purple-600" /> {t('offers.title')}
          </h1>
          <p className="text-gray-500 mt-1">{offers.length} ofert</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-base">
            <Plus size={18} /> {t('offers.newOffer')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('offers.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-base" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 border border-gray-300 rounded-lg text-base">
          <option value="all">Wszystkie statusy</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{t(v.labelKey)}</option>)}
        </select>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 opacity-50" />
          <p>{offers.length === 0 ? t('offers.noOffers') : t('common.noResults')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(o => {
            const s = STATUS[o.status] || STATUS.draft;
            return (
              <div key={o.id} onClick={() => onSelect(o)}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                    <FileText size={24} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 text-base flex items-center gap-2">
                      <span className="font-mono text-sm text-purple-600">{o.offer_number}</span>
                      {o.name}
                      {o.unread_messages > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full leading-none">{o.unread_messages}</span>}
                    </div>
                    <div className="text-base text-gray-500 flex items-center gap-3 mt-0.5">
                      {o.client_name && <span>{o.client_name}</span>}
                      <span>{o.items_count || 0} pozycji</span>
                      <span>{o.creator_name}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {o.total_value > 0 && (
                    <span className="font-semibold text-gray-800 text-base">
                      {Number(o.total_value).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} {o.currency}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 rounded text-sm font-medium ${s.color}`}>{t(s.labelKey)}</span>
                  {canDelete && (
                    <button onClick={e => { e.stopPropagation(); setDeleteTarget(o); }}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={t('offers.newOffer')} maxWidth="max-w-xl">
        <NewOfferForm onSave={handleCreate} onCancel={() => setShowForm(false)} />
      </Modal>
      <ConfirmDialog isOpen={!!deleteTarget} type="danger"
        title={t('common.delete')} message={`Usunąć ofertę "${deleteTarget?.offer_number}"?`}
        onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}

// ==================== NEW OFFER FORM ====================

function NewOfferForm({ onSave, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', description: '', client_name: '', client_email: '', client_phone: '', client_company: '', currency: 'PLN', valid_until: '', source_pricelist_id: '', company_id: null });
  const [pricelists, setPricelists] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  useEffect(() => {
    api.get('/offers/pricelists/list').then(r => setPricelists(r.data.pricelists)).catch(() => {});
    api.get('/system/companies').then(r => setCompanies(r.data.companies)).catch(() => {});
  }, []);

  const selectCompany = (company) => {
    setForm(f => ({
      ...f,
      company_id: company.id,
      client_company: company.name,
      client_name: company.contact_person || f.client_name,
      client_email: company.contact_email || company.email || f.client_email,
      client_phone: company.contact_phone || company.phone || f.client_phone,
    }));
    setCompanySearch('');
  };

  const filteredCompanies = companies.filter(c =>
    companySearch.length >= 1 && (
      c.name.toLowerCase().includes(companySearch.toLowerCase()) ||
      (c.nip && c.nip.includes(companySearch)) ||
      (c.contact_person && c.contact_person.toLowerCase().includes(companySearch.toLowerCase()))
    )
  );

  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ ...form, source_pricelist_id: form.source_pricelist_id || null }); }} className="space-y-3">
      <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('common.name')} *</label>
        <input type="text" value={form.name} onChange={set('name')} className={ic} required autoFocus /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('common.description')}</label>
        <textarea value={form.description} onChange={set('description')} className={ic} rows={2} /></div>

      <div className="border-t pt-3"><h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Klient</h3>
        <div className="relative mb-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Wybierz firmę z bazy</label>
          <div className="flex gap-2">
            <input type="text" value={companySearch} onChange={e => setCompanySearch(e.target.value)}
              placeholder="Szukaj po nazwie, NIP lub osobie kontaktowej..."
              className={ic} />
            {form.company_id && (
              <button type="button" onClick={() => { setForm(f => ({ ...f, company_id: null, client_company: '', client_name: '', client_email: '', client_phone: '' })); setCompanySearch(''); }}
                className="px-2 py-1 text-xs text-red-500 hover:bg-red-50 rounded border border-red-200">Wyczyść</button>
            )}
          </div>
          {filteredCompanies.length > 0 && !form.company_id && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredCompanies.map(c => (
                <button key={c.id} type="button" onClick={() => selectCompany(c)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-0">
                  <div className="font-medium text-gray-800">{c.name}</div>
                  <div className="text-xs text-gray-500">
                    {c.nip && `NIP: ${c.nip}`} {c.contact_person && `• ${c.contact_person}`} {c.address_city && `• ${c.address_city}`}
                  </div>
                </button>
              ))}
            </div>
          )}
          {form.company_id && (
            <div className="mt-1 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700">
              Wybrano: <strong>{form.client_company}</strong>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Firma</label><input type="text" value={form.client_company} onChange={set('client_company')} className={ic} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Osoba</label><input type="text" value={form.client_name} onChange={set('client_name')} className={ic} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('common.email')}</label><input type="email" value={form.client_email} onChange={set('client_email')} className={ic} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('common.phone')}</label><input type="text" value={form.client_phone} onChange={set('client_phone')} className={ic} /></div>
        </div>
      </div>

      <div className="border-t pt-3 grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('common.currency')}</label>
          <select value={form.currency} onChange={set('currency')} className={ic}>
            <option value="PLN">PLN</option><option value="EUR">EUR</option><option value="USD">USD</option>
          </select></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('offers.validityLabel')}</label>
          <input type="date" value={form.valid_until} onChange={set('valid_until')} className={ic} /></div>
      </div>

      {pricelists.length > 0 && (
        <div className="border-t pt-3">
          <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><Copy size={12} /> Kopiuj strukturę z cennika</label>

          <select value={form.source_pricelist_id} onChange={set('source_pricelist_id')} className={ic}>
            <option value="">— Pusty (od zera) —</option>
            {pricelists.map(p => <option key={p.id} value={p.id}>{p.name} ({p.items_count} poz.)</option>)}
          </select>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">{t('common.create')}</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300">{t('common.cancel')}</button>
      </div>
    </form>
  );
}

// ==================== TREE NODE (reused from pricelists pattern) ====================

function TreeNode({ node, depth, canEdit, canDelete, canCreate, onEdit, onDelete, onAdd, onMove,
  onSpreadsheetSave, activeSpreadsheet, setActiveSpreadsheet, variables, parentExports = [], onDuplicate, onCopyFromPricelist, onToggleVisible, parentHidden = false }) {
  const [expanded, setExpanded] = useState(true);
  const isGroup = node.item_type === 'group';
  const hasChildren = node.children.length > 0;
  const nodeRef = useRef(null);
  const isOpen = activeSpreadsheet === node.id;
  const hasSheet = node.spreadsheet?.columns?.length > 0;
  const fmt = (p) => Number(p||0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div ref={nodeRef} draggable={canEdit}
        onDragStart={e => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('application/x-offer-item', JSON.stringify({ id: node.id }));
          setTimeout(() => { if (nodeRef.current) nodeRef.current.style.opacity = '0.4'; }, 0);
        }}
        onDragEnd={e => {
          e.stopPropagation();
          if (nodeRef.current) nodeRef.current.style.opacity = '1';
          if (onMove && e.dataTransfer.dropEffect !== 'none') {
            // handled by onDrop
          }
        }}
        onDragOver={e => {
          e.preventDefault(); e.stopPropagation();
          const rect = nodeRef.current.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const h = rect.height;
          let pos = y < h / 2 ? 'before' : 'after';
          if (isGroup && y > h * 0.25 && y < h * 0.75) pos = 'inside';
          nodeRef.current.dataset.dropPos = pos;
          // Visual feedback
          nodeRef.current.classList.remove('border-t-2', 'border-b-2', 'ring-2');
          if (pos === 'before') nodeRef.current.classList.add('border-t-2', 'border-t-purple-500');
          else if (pos === 'after') nodeRef.current.classList.add('border-b-2', 'border-b-purple-500');
          else nodeRef.current.classList.add('ring-2', 'ring-purple-500', 'ring-inset');
        }}
        onDragLeave={e => {
          nodeRef.current?.classList.remove('border-t-2', 'border-b-2', 'ring-2', 'border-t-purple-500', 'border-b-purple-500', 'ring-purple-500', 'ring-inset');
        }}
        onDrop={e => {
          e.preventDefault(); e.stopPropagation();
          nodeRef.current?.classList.remove('border-t-2', 'border-b-2', 'ring-2', 'border-t-purple-500', 'border-b-purple-500', 'ring-purple-500', 'ring-inset');
          // Try internal offer move first
          const offerData = e.dataTransfer.getData('application/x-offer-item');
          if (offerData) {
            try {
              const data = JSON.parse(offerData);
              if (data.id && data.id !== node.id && onMove) {
                const pos = nodeRef.current?.dataset.dropPos || 'after';
                onMove(data.id, node.id, pos);
              }
            } catch {}
            return;
          }
          // Try pricelist-to-offer copy
          const plData = e.dataTransfer.getData('application/x-pricelist-to-offer');
          if (plData && onCopyFromPricelist) {
            try {
              const data = JSON.parse(plData);
              const pos = nodeRef.current?.dataset.dropPos || 'after';
              const parentId = pos === 'inside' ? node.id : (node.parent_id || null);
              onCopyFromPricelist(data.itemId, parentId, data.sharedId, node.variant_id);
            } catch {}
          }
        }}
        className={`flex items-center gap-1 py-1.5 px-2 rounded-md hover:bg-gray-50 group transition-all select-none ${isOpen ? 'bg-purple-50' : ''} ${(node.visible === false || parentHidden) ? 'opacity-40' : ''}`}
        style={{ paddingLeft: `${depth * 20 + 4}px` }}>
        {canEdit && <span className="cursor-grab opacity-0 group-hover:opacity-40 hover:!opacity-100 flex-shrink-0" onMouseDown={e => e.preventDefault()}><GripVertical size={14} /></span>}
        {isGroup ? (
          <button onClick={() => setExpanded(!expanded)} className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded">
            {expanded ? <ChevronDown size={15} className="text-gray-500" /> : <ChevronRight size={15} className="text-gray-500" />}
          </button>
        ) : <span className="w-[20px] flex-shrink-0" />}
        {isGroup ? <FolderOpen size={16} className="text-amber-500 flex-shrink-0" /> : <Package size={16} className="text-gray-400 flex-shrink-0" />}
        <span className={`flex-1 text-sm truncate cursor-pointer ${isGroup ? 'font-medium text-gray-800' : 'text-gray-700'} ${isOpen ? 'text-purple-700' : ''}`}
          onClick={() => setActiveSpreadsheet(isOpen ? null : node.id)} title={node.description || ''}>
          {node.name}
          {node.description && <span className="ml-1.5 text-xs text-gray-400 font-normal">{node.description.length > 30 ? node.description.substring(0, 30) + '...' : node.description}</span>}
        </span>
        {hasSheet && <Table2 size={14} className={isOpen ? 'text-purple-500' : 'text-gray-300'} />}
        <span className="text-sm font-medium text-gray-700 w-24 text-right mr-1">{fmt(node.result_value)}</span>
        {parseFloat(node.profit_value) !== 0 && (
          <span className={`text-xs w-20 text-right mr-1 ${parseFloat(node.profit_value) > 0 ? 'text-green-600' : 'text-red-500'}`} title="Profit">
            {parseFloat(node.profit_value) > 0 ? '+' : ''}{fmt(node.profit_value)}
          </span>
        )}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0">
          {canCreate && isGroup && <button onClick={() => onAdd(node.id, 'item')} className="p-0.5 hover:bg-purple-50 rounded"><Plus size={14} className="text-purple-600" /></button>}
          {canCreate && isGroup && <button onClick={() => onAdd(node.id, 'group')} className="p-0.5 hover:bg-amber-50 rounded"><FolderOpen size={14} className="text-amber-500" /></button>}
          {onToggleVisible && <button onClick={() => onToggleVisible(node)} className={`p-0.5 rounded ${node.visible !== false ? 'hover:bg-gray-100' : 'hover:bg-red-50'}`}
            title={node.visible !== false ? 'Widoczny' : 'Ukryty'}>
            {node.visible !== false ? <Eye size={14} className="text-gray-300" /> : <EyeOff size={14} className="text-red-400" />}
          </button>}
          {canCreate && onDuplicate && <button onClick={() => onDuplicate(node)} className="p-0.5 hover:bg-blue-50 rounded" title="Duplikuj"><Copy size={14} className="text-blue-500" /></button>}
          {canEdit && <button onClick={() => onEdit(node)} className="p-0.5 hover:bg-gray-100 rounded"><Pencil size={14} className="text-gray-500" /></button>}
          {canDelete && <button onClick={() => onDelete(node)} className="p-0.5 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>}
        </div>
      </div>
      {isOpen && (
        <div className="mr-1 mb-1 mt-0.5" style={{ paddingLeft: `${depth * 20 + 24}px` }}>
          <MiniSpreadsheet data={node.spreadsheet} readOnly={!canEdit} variables={variables} parentExports={parentExports}
            onChange={(ss, rv) => onSpreadsheetSave(node.id, ss, rv)} />
        </div>
      )}
      {isGroup && expanded && hasChildren && (() => {
        const nodeExports = [];
        const ss = node.spreadsheet;
        if (ss?.exports && ss.rows) {
          const resolveCell = (row, colKey, visited = new Set()) => {
            const raw = row[colKey];
            if (raw === undefined || raw === null || raw === '') return 0;
            const s = String(raw);
            if (s.startsWith("'")) return 0;
            if (!s.startsWith('=')) return parseFloat(s) || 0;
            if (visited.has(colKey)) return 0;
            visited.add(colKey);
            let expr = s.substring(1);
            expr = expr.replace(/(?<![a-zA-Z_0-9])([a-zA-Z])(?![a-zA-Z_0-9])/g, (m, c) => {
              const k = c.toUpperCase();
              if (!row.hasOwnProperty(k) && !row.hasOwnProperty(c)) return m;
              return String(resolveCell(row, k, new Set(visited)));
            });
            try {
              if (/^[\d\s+\-*/().,%<>=?:!&|]+$/.test(expr)) {
                const r = eval(expr);
                if (typeof r === 'number' && isFinite(r)) return Math.round(r * 100) / 100;
              }
            } catch {}
            return 0;
          };

          Object.entries(ss.exports).forEach(([ref, meta]) => {
            const [ri, ck] = ref.split(':');
            const row = ss.rows?.[parseInt(ri)];
            if (row) {
              nodeExports.push({ label: meta.label, description: meta.description, value: resolveCell(row, ck), source: node.name });
            }
          });
        }
        return (
          <div>{node.children.map(child => (
            <TreeNode key={child.id} node={child} depth={depth + 1}
              canEdit={canEdit} canDelete={canDelete} canCreate={canCreate}
              onEdit={onEdit} onDelete={onDelete} onAdd={onAdd} onMove={onMove}
              onSpreadsheetSave={onSpreadsheetSave} activeSpreadsheet={activeSpreadsheet}
              setActiveSpreadsheet={setActiveSpreadsheet} variables={variables}
              parentExports={[...parentExports, ...nodeExports]} onDuplicate={onDuplicate} onCopyFromPricelist={onCopyFromPricelist}
              onToggleVisible={onToggleVisible} parentHidden={parentHidden || node.visible === false} />
          ))}</div>
        );
      })()}
    </div>
  );
}

// ==================== SPLIT PANEL ====================

function SplitPanels({ left, right }) {
  const [splitPct, setSplitPct] = useState(55);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const handleMouseDown = (e) => {
    e.preventDefault(); dragging.current = true;
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
    const move = (e) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setSplitPct(Math.min(80, Math.max(20, ((e.clientX - rect.left) / rect.width) * 100)));
    };
    const up = () => {
      dragging.current = false; document.body.style.cursor = ''; document.body.style.userSelect = '';
      document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };

  return (
    <div ref={containerRef} className="flex-1 flex min-h-0 h-full">
      <div style={{ width: `${splitPct}%` }} className="min-w-0 overflow-auto h-full">{left}</div>
      <div onMouseDown={handleMouseDown}
        className="w-2 flex-shrink-0 cursor-col-resize group flex items-center justify-center hover:bg-purple-100 transition-colors">
        <div className="w-0.5 h-8 bg-gray-300 group-hover:bg-purple-500 rounded-full transition-colors" />
      </div>
      <div style={{ width: `${100 - splitPct}%` }} className="min-w-0 overflow-auto h-full">{right}</div>
    </div>
  );
}

// ==================== PRICELIST READ-ONLY PANEL (right side) ====================

function PricelistReadPanel({ toast, offerId, onCopyToOffer }) {
  const { t } = useTranslation();
  const [pricelists, setPricelists] = useState([]);
  const [sharedPricelists, setSharedPricelists] = useState([]);
  const [tab, setTab] = useState('own'); // own | shared
  const [search, setSearch] = useState('');
  const [selectedId, _setSelectedId] = useState(() => {
    const saved = localStorage.getItem('swibi_offer_pricelist');
    return saved ? parseInt(saved) : null;
  });
  const [selectedSharedId, setSelectedSharedId] = useState(null);
  const setSelectedId = (id) => {
    _setSelectedId(id);
    if (id) localStorage.setItem('swibi_offer_pricelist', id);
    else localStorage.removeItem('swibi_offer_pricelist');
  };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSheet, setActiveSheet] = useState(null);
  const [shareDiscounts, setShareDiscounts] = useState([]);
  const [shareMargins, setShareMargins] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    api.get('/offers/pricelists/list').then(r => {
      setPricelists(r.data.pricelists);
      setSharedPricelists(r.data.shared || []);
    }).catch(() => {});
  }, []);

  // Debounced global search
  useEffect(() => {
    if (selectedId || selectedSharedId) return; // don't search when viewing a pricelist
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!search || search.length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(() => {
      api.get(`/offers/pricelists/search?q=${encodeURIComponent(search)}`).then(r => {
        setSearchResults(r.data.pricelists || []);
      }).catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [search, selectedId, selectedSharedId]);

  useEffect(() => {
    if (!selectedId && !selectedSharedId) return;
    setLoading(true);
    if (selectedSharedId) {
      api.get(`/pricelists/shared/${selectedSharedId}`).then(r => {
        setItems(r.data.items);
        setShareDiscounts(r.data.discounts || []);
        setShareMargins(r.data.margins || []);
      }).catch(() => { setItems([]); setShareDiscounts([]); setShareMargins([]); })
        .finally(() => setLoading(false));
    } else {
      api.get(`/pricelists/${selectedId}`).then(r => setItems(r.data.items)).catch(() => setItems([]))
        .finally(() => setLoading(false));
      setShareDiscounts([]); setShareMargins([]);
    }
  }, [selectedId, selectedSharedId]);

  const selectPricelist = (id) => { setSelectedSharedId(null); setSelectedId(id); setActiveSheet(null); setSearch(''); };
  const selectShared = (shareId) => { setSelectedId(null); setSelectedSharedId(shareId); setActiveSheet(null); setSearch(''); };

  const filteredOwn = pricelists.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));
  const filteredShared = sharedPricelists.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.tenant_name.toLowerCase().includes(search.toLowerCase()));

  const isOwn = selectedId && !selectedSharedId;

  const handleSaveSpreadsheet = async (itemId, spreadsheet, resultValue) => {
    if (!isOwn) return;
    try {
      await api.put(`/pricelists/items/${itemId}/spreadsheet`, { spreadsheet, result_value: resultValue });
      // Reload items
      api.get(`/pricelists/${selectedId}`).then(r => setItems(r.data.items)).catch(() => {});
    } catch {}
  };

  const tree = buildTree(items);

  const renderNode = (node, depth) => {
    const isGroup = node.item_type === 'group';
    const isOpen = activeSheet === node.id;
    const fmt = (p) => Number(p||0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
      <div key={node.id}>
        <div className="flex items-center gap-1 py-1.5 px-2 rounded hover:bg-gray-50 group select-none"
          style={{ paddingLeft: `${depth * 20 + 4}px` }}
          draggable onDragStart={e => {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('application/x-pricelist-to-offer', JSON.stringify({ itemId: node.id, sharedId: selectedSharedId || null }));
          }}>
          <span className="cursor-grab opacity-0 group-hover:opacity-40 hover:!opacity-100 flex-shrink-0" onMouseDown={e => e.preventDefault()}><GripVertical size={14} /></span>
          {isGroup ? (
            <button onClick={() => setActiveSheet(isOpen ? null : node.id)} className="p-0.5">
              {node.children.length > 0 ? <ChevronDown size={15} className="text-gray-400" /> : <span className="w-4" />}
            </button>
          ) : <span className="w-5" />}
          {isGroup ? <FolderOpen size={14} className="text-amber-500" /> : <Package size={14} className="text-gray-400" />}
          <span className={`flex-1 text-xs truncate cursor-grab ${isGroup ? 'font-medium text-gray-700' : 'text-gray-600'}`}
            onClick={() => setActiveSheet(isOpen ? null : node.id)}>
            {node.name}
          </span>
          {selectedSharedId && node.spreadsheet?.rows?.length > 0 ? (() => {
            // Show price breakdown: base - discount + margin
            const row = node.spreadsheet.rows[0];
            const basePrice = parseFloat(String(row?.D || '').replace(/^'/, '').replace(/\s/g,'').replace(',','.')) || 0;
            if (basePrice <= 0) return <span className="text-xs text-gray-500 w-20 text-right">{fmt(node.result_value)}</span>;

            // Calculate all discounts — unconditional + conditional with details
            let totalMarkup = 0;
            let totalDiscount = 0;
            let conditionalDetails = [];
            shareDiscounts.filter(d => d.visible_to_recipient).forEach(d => {
              const val = parseFloat(d.value) || 0;
              const amount = d.discount_type === 'percent' ? basePrice * val / 100 : val;
              const hasCondition = d.condition_min != null || d.condition_max != null;
              if (d.is_markup) {
                if (!hasCondition) totalMarkup += amount;
              } else {
                if (hasCondition) {
                  const cost = basePrice - amount;
                  const rangeMin = d.condition_min != null ? parseFloat(d.condition_min) : null;
                  const rangeMax = d.condition_max != null ? parseFloat(d.condition_max) : null;
                  const rangeLabel = rangeMin != null && rangeMax != null ? `${rangeMin}–${rangeMax}` : rangeMin != null ? `od ${rangeMin}` : `do ${rangeMax}`;
                  conditionalDetails.push({ amount, cost, val, type: d.discount_type, rangeLabel });
                } else {
                  totalDiscount += amount;
                }
              }
            });
            const bestConditionalDiscount = conditionalDetails.length > 0 ? Math.min(...conditionalDetails.map(c => c.amount)) : 0;
            const displayDiscount = totalDiscount > 0 ? totalDiscount : bestConditionalDiscount;

            // Margin (my markup on base price)
            const globalMargin = shareMargins.find(m => m.scope === 'global' && !m.target_item_id);
            let marginAmount = 0;
            if (globalMargin) {
              const mv = parseFloat(globalMargin.margin_value) || 0;
              marginAmount = globalMargin.margin_type === 'percent' ? basePrice * mv / 100 : mv;
            }
            const sellPrice = basePrice + totalMarkup + marginAmount;
            const myCost = basePrice - displayDiscount;

            // Unit from column C
            const unitC = String(row?.C || '').replace(/^'/, '').trim();

            // Tooltip
            const hasConditional = conditionalDetails.length > 0;
            const marginPct = globalMargin && globalMargin.margin_type === 'percent' ? `${parseFloat(globalMargin.margin_value)}%` : null;
            const marginAbs = globalMargin && globalMargin.margin_type !== 'percent' ? `${parseFloat(globalMargin.margin_value)} zł` : null;
            const tooltipLines = [];
            tooltipLines.push(`${t('pricelists.tooltipBasePrice')}: ${fmt(basePrice)} zł/${unitC || 'szt'}`);
            if (displayDiscount > 0.01) tooltipLines.push(`${t('pricelists.tooltipAfterDiscount')}: ${fmt(myCost)} zł/${unitC || 'szt'}`);
            if (marginAmount > 0.01 || totalMarkup > 0.01) tooltipLines.push(`${t('pricelists.tooltipWithMargin')}${marginPct ? ` (${marginPct})` : marginAbs ? ` (${marginAbs})` : ''}: ${fmt(sellPrice)} zł/${unitC || 'szt'}`);
            if (hasConditional) {
              tooltipLines.push('');
              tooltipLines.push(`${t('pricelists.tooltipConditionalDiscounts')}:`);
              conditionalDetails.forEach(c => {
                tooltipLines.push(`  ${c.rangeLabel}${unitC ? ' ' + unitC : ' szt'}: ${t('pricelists.tooltipDiscount')} ${c.val}${c.type === 'percent' ? '%' : ' zł'} → ${fmt(c.cost)} zł/${unitC || 'szt'}`);
              });
            }
            const tooltip = tooltipLines.join('\n');

            return (
              <span className="text-xs text-right flex-shrink-0 ml-1 flex items-center gap-0.5"
                title={tooltip || undefined}>
                <span className="text-gray-400">{fmt(basePrice)}</span>
                {displayDiscount > 0.01 && <><span className="text-gray-300">|</span><span className="text-green-600">{fmt(myCost)}</span></>}
                {(totalMarkup > 0.01 || marginAmount > 0.01) && <><span className="text-gray-300">|</span><span className="text-blue-600 font-medium">{fmt(sellPrice)}</span></>}
                {hasConditional && <span className="text-green-500 text-[9px] cursor-help">*</span>}
                {unitC && <span className="text-gray-400">/{unitC}</span>}
              </span>
            );
          })() : (
            <span className="text-xs text-gray-500 w-20 text-right">{fmt(node.result_value)}</span>
          )}
          {isOwn && (
            <button onClick={async () => {
              try {
                await api.post(`/pricelists/items/${node.id}/copy`, { target_pricelist_id: selectedId, target_parent_id: node.parent_id || null });
                api.get(`/pricelists/${selectedId}`).then(r => setItems(r.data.items)).catch(() => {});
              } catch {}
            }} className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-blue-50 rounded" title="Duplikuj w cenniku">
              <CopyPlus size={14} className="text-blue-500" />
            </button>
          )}
          <button onClick={() => onCopyToOffer(node.id, selectedSharedId)}
            className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-purple-50 rounded" title="Kopiuj do oferty">
            <Copy size={14} className="text-purple-600" />
          </button>
        </div>
        {isOpen && node.spreadsheet?.columns?.length > 0 && (
          <div className="mx-2 mb-1" style={{ paddingLeft: `${depth * 16 + 20}px` }}>
            <MiniSpreadsheet data={node.spreadsheet} readOnly={!isOwn}
              onChange={isOwn ? (ss, rv) => handleSaveSpreadsheet(node.id, ss, rv) : undefined} />
          </div>
        )}
        {isGroup && node.children.map(c => renderNode(c, depth + 1))}
      </div>
    );
  };

  const isSelected = selectedId || selectedSharedId;

  return (
    <div className="border border-gray-200 rounded-lg bg-white flex flex-col overflow-hidden">
      {/* Header: tabs + search */}
      <div className="bg-emerald-50 border-b border-emerald-200">
        {!isSelected && (<>
          <div className="flex items-center px-2 pt-2 gap-1">
            <button onClick={() => { setTab('own'); }}
              className={`flex-1 px-3 py-2 rounded-t text-sm font-medium transition ${tab === 'own' ? 'bg-white text-emerald-700 border border-b-0 border-emerald-200' : 'text-gray-500 hover:text-gray-700'}`}>
              {t('pricelists.ownTab')} ({pricelists.length})
            </button>
            <button onClick={() => { setTab('shared'); }}
              className={`flex-1 px-3 py-2 rounded-t text-sm font-medium transition ${tab === 'shared' ? 'bg-white text-blue-700 border border-b-0 border-blue-200' : 'text-gray-500 hover:text-gray-700'}`}>
              {t('pricelists.sharedTab')} ({sharedPricelists.length})
            </button>
          </div>
          <div className="px-2 pb-2">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('pricelists.searchPricelist')} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mt-1" />
          </div>
        </>)}
        {isSelected && (
          <div className="px-3 py-2 flex items-center gap-2">
            <button onClick={() => { setSelectedId(null); setSelectedSharedId(null); setItems([]); }}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs font-medium flex-shrink-0">← Lista</button>
            <span className="text-sm font-medium text-gray-700 truncate">
              {selectedSharedId ? sharedPricelists.find(s => s.share_id === selectedSharedId)?.name : pricelists.find(p => p.id === selectedId)?.name}
            </span>
            {selectedSharedId && (
              <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded ml-1">
                {sharedPricelists.find(s => s.share_id === selectedSharedId)?.tenant_name}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-1" onDragOver={e => e.preventDefault()}>
        {/* Global search results — tree per pricelist */}
        {!isSelected && search.length >= 2 && (
          <div className="space-y-2">
            {searching ? (
              <div className="text-center py-8 text-sm text-gray-400"><Loader2 size={16} className="inline animate-spin mr-1" />{t('common.loading')}</div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">{t('common.noResults')}</div>
            ) : searchResults.map(pl => {
              const plTree = buildTree(pl.items);
              const renderSearchNode = (node, depth) => {
                const isMatch = node.is_match;
                const isGroup = node.item_type === 'group';
                const fmt = v => Number(v||0).toLocaleString('pl-PL', {minimumFractionDigits:2, maximumFractionDigits:2});
                return (
                  <div key={node.id}>
                    <div draggable onDragStart={e => {
                        e.stopPropagation();
                        e.dataTransfer.effectAllowed = 'copy';
                        e.dataTransfer.setData('application/x-pricelist-to-offer', JSON.stringify({ itemId: node.id, sharedId: pl.share_id || null }));
                      }}
                      className={`flex items-center gap-1 py-1 px-1 rounded text-xs group cursor-grab ${isMatch ? 'bg-yellow-50 font-medium' : 'text-gray-400'}`}
                      style={{ paddingLeft: `${depth * 14 + 4}px` }}>
                      {isGroup ? <FolderOpen size={12} className={isMatch ? 'text-amber-500' : 'text-amber-300'} /> : <Package size={12} className={isMatch ? 'text-gray-600' : 'text-gray-300'} />}
                      <span className={`truncate ${isMatch ? 'text-gray-800' : 'text-gray-400'}`}>{node.name}</span>
                      <span className="flex items-center gap-1 flex-shrink-0 ml-auto">
                        {isMatch && (() => {
                          const row = node.spreadsheet?.rows?.[0];
                          const priceRaw = String(row?.D || '').replace(/^'/, '').replace(/\s/g,'').replace(',','.');
                          const basePrice = parseFloat(priceRaw) || 0;
                          if (basePrice <= 0) return node.result_value > 0 ? <span className="text-[10px] text-gray-500">{fmt(node.result_value)}</span> : null;

                          // Calculate all discounts — unconditional + conditional with details
                          let totalMarkup = 0, totalDiscount = 0, conditionalDetails = [];
                          (pl.discounts || []).forEach(d => {
                            const val = parseFloat(d.value) || 0;
                            const amount = d.discount_type === 'percent' ? basePrice * val / 100 : val;
                            const hasCondition = d.condition_min != null || d.condition_max != null;
                            if (d.is_markup) { if (!hasCondition) totalMarkup += amount; }
                            else if (hasCondition) {
                              const cost = basePrice - amount;
                              const rangeMin = d.condition_min != null ? parseFloat(d.condition_min) : null;
                              const rangeMax = d.condition_max != null ? parseFloat(d.condition_max) : null;
                              const rangeLabel = rangeMin != null && rangeMax != null ? `${rangeMin}–${rangeMax}` : rangeMin != null ? `od ${rangeMin}` : `do ${rangeMax}`;
                              conditionalDetails.push({ amount, cost, val, type: d.discount_type, rangeLabel });
                            } else { totalDiscount += amount; }
                          });
                          const bestConditionalDiscount = conditionalDetails.length > 0 ? Math.min(...conditionalDetails.map(c => c.amount)) : 0;
                          const displayDiscount = totalDiscount > 0 ? totalDiscount : bestConditionalDiscount;
                          const globalMargin = (pl.margins || []).find(m => m.scope === 'global' && !m.target_item_id);
                          let marginAmount = 0;
                          if (globalMargin) {
                            const mv = parseFloat(globalMargin.margin_value) || 0;
                            marginAmount = globalMargin.margin_type === 'percent' ? basePrice * mv / 100 : mv;
                          }
                          const sellPrice = basePrice + totalMarkup + marginAmount;
                          const myCost = basePrice - displayDiscount;
                          const unitC = String(row?.C || '').replace(/^'/, '').trim();
                          const hasConditional = conditionalDetails.length > 0;
                          const marginPct = globalMargin && globalMargin.margin_type === 'percent' ? `${parseFloat(globalMargin.margin_value)}%` : null;
                          const marginAbs = globalMargin && globalMargin.margin_type !== 'percent' ? `${parseFloat(globalMargin.margin_value)} zł` : null;
                          const tooltipLines = [];
                          tooltipLines.push(`${t('pricelists.tooltipBasePrice')}: ${fmt(basePrice)} zł/${unitC || 'szt'}`);
                          if (marginAmount > 0.01 || totalMarkup > 0.01) tooltipLines.push(`${t('pricelists.tooltipWithMargin')}${marginPct ? ` (${marginPct})` : marginAbs ? ` (${marginAbs})` : ''}: ${fmt(sellPrice)} zł/${unitC || 'szt'}`);
                          if (displayDiscount > 0.01) tooltipLines.push(`${t('pricelists.tooltipAfterDiscount')}: ${fmt(myCost)} zł/${unitC || 'szt'}`);
                          if (hasConditional) {
                            tooltipLines.push('');
                            tooltipLines.push(`${t('pricelists.tooltipConditionalDiscounts')}:`);
                            conditionalDetails.forEach(c => {
                              tooltipLines.push(`  ${c.rangeLabel}${unitC ? ' ' + unitC : ' szt'}: ${t('pricelists.tooltipDiscount')} ${c.val}${c.type === 'percent' ? '%' : ' zł'} → ${fmt(c.cost)} zł/${unitC || 'szt'}`);
                            });
                          }
                          const tooltip = tooltipLines.join('\n');
                          return (
                            <span className="flex items-center gap-0.5" title={tooltip || undefined}>
                              <span className="text-[10px] text-gray-400">{fmt(basePrice)}</span>
                              {displayDiscount > 0.01 && <><span className="text-gray-300">|</span><span className="text-[10px] text-green-600">{fmt(myCost)}</span></>}
                              {(totalMarkup > 0.01 || marginAmount > 0.01) && <><span className="text-gray-300">|</span><span className="text-[10px] text-blue-600 font-medium">{fmt(sellPrice)}</span></>}
                              {hasConditional && <span className="text-green-500 text-[8px] cursor-help">*</span>}
                              {unitC && <span className="text-[10px] text-gray-400">/{unitC}</span>}
                            </span>
                          );
                        })()}
                        {!isMatch && node.result_value > 0 && <span className="text-gray-400 text-[10px]">{fmt(node.result_value)}</span>}
                        {isMatch && (
                          <button onClick={() => onCopyToOffer(node.id, pl.share_id)}
                            className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-purple-50 rounded" title="Kopiuj do oferty">
                            <Copy size={12} className="text-purple-600" />
                          </button>
                        )}
                      </span>
                    </div>
                    {(node.children || []).map(c => renderSearchNode(c, depth + 1))}
                  </div>
                );
              };
              return (
                <div key={`${pl.share_id || 'own'}-${pl.pricelist_id}`} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center gap-1.5 text-xs">
                    <FileText size={13} className="text-emerald-500 flex-shrink-0" />
                    {pl.tenant_name && <span className="text-blue-500 font-medium">{pl.tenant_name} —</span>}
                    <span className="text-gray-700 font-medium truncate">{pl.pricelist_name}</span>
                  </div>
                  <div className="p-1">
                    {plTree.map(n => renderSearchNode(n, 0))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Pricelist browser (no selection yet, no active search) */}
        {!isSelected && search.length < 2 && tab === 'own' && (
          <div className="space-y-0.5">
            {filteredOwn.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">{t('pricelists.noPricelists')}</div>
            ) : filteredOwn.map(p => (
              <button key={p.id} onClick={() => selectPricelist(p.id)}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-emerald-50 flex items-center justify-between group text-sm">
                <span className="text-gray-700 truncate font-medium">{p.name}</span>
                <span className="text-gray-400 flex-shrink-0 text-xs">{p.items_count} poz.</span>
              </button>
            ))}
          </div>
        )}
        {!isSelected && search.length < 2 && tab === 'shared' && (
          <div className="space-y-0.5">
            {filteredShared.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">{t('pricelists.noPricelists')}</div>
            ) : filteredShared.map(s => (
              <button key={s.share_id} onClick={() => selectShared(s.share_id)}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 flex items-center justify-between group text-sm">
                <div className="truncate">
                  <span className="text-blue-500 font-medium">{s.tenant_name}</span>
                  <span className="text-gray-700 ml-1.5">— {s.name}</span>
                </div>
                <span className="text-gray-400 flex-shrink-0 text-xs">{s.items_count} poz.</span>
              </button>
            ))}
          </div>
        )}

        {/* Selected pricelist tree */}
        {isSelected && loading ? (
          <div className="flex items-center justify-center h-32 text-xs text-gray-400">{t('common.loading')}</div>
        ) : loading ? (
          <div className="flex items-center justify-center h-32 text-xs text-gray-400">{t('common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-xs text-gray-400">Cennik pusty</div>
        ) : (
          tree.map(n => renderNode(n, 0))
        )}
      </div>
      <div className="px-3 py-1.5 bg-gray-50 border-t text-xs text-gray-400 flex items-center gap-1">
        <Copy size={12} /> Przeciągnij lub kliknij <span className="text-purple-500">kopiuj</span> aby dodać do oferty
      </div>
    </div>
  );
}

// ==================== OFFER DETAIL (dual panel) ====================

function OfferDetail({ offer: initialOffer, onBack, canCreate, canEdit, canDelete, toast }) {
  const { t } = useTranslation();
  const [offer, setOffer] = useState(initialOffer);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variables, setVariables] = useState({ global: [], local: [] });
  const [activeSpreadsheet, setActiveSpreadsheet] = useState(null);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [addParentId, setAddParentId] = useState(null);
  const [addItemType, setAddItemType] = useState('item');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showOfferDetails, setShowOfferDetails] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('items'); // items | content
  const [showNprDialog, setShowNprDialog] = useState(false);
  const [nprSending, setNprSending] = useState(false);
  const { user: currentUser } = useAuth();
  const [collaborators, setCollaborators] = useState([]);
  const [variants, setVariants] = useState([]);

  const saveOfferField = async (fields) => {
    try { const r = await api.put(`/offers/${offer.id}`, fields); setOffer(o => ({...o, ...r.data.offer})); }
    catch { toast.error(t('common.error')); }
  };

  const load = useCallback(async () => {
    try {
      const [ir, vr] = await Promise.all([
        api.get(`/offers/${offer.id}`),
        api.get(`/offers/${offer.id}/all-variables`)
      ]);
      setOffer(ir.data.offer);
      setItems(ir.data.items);
      setCollaborators(ir.data.collaborators || []);
      setVariants(ir.data.variants || []);
      setVariables(vr.data);
    } catch { toast.error(t('common.error')); onBack(); return; }
    finally { setLoading(false); }
  }, [offer.id, toast]);

  useEffect(() => { load(); }, [load]);

  const offerHasLocalVars = (variables.local || []).length > 0;

  const handleUpdateOfferVar = async (key, newValue) => {
    setVariables(prev => ({
      ...prev,
      local: (prev.local || []).map(v => v.key === key ? { ...v, value: newValue } : v)
    }));
    try {
      await api.post(`/offers/${offer.id}/variables`, { key, value: newValue });
      // Recalculate after variable change
      await api.post(`/offers/${offer.id}/recalculate`);
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleDuplicate = async (node) => {
    try {
      await api.post(`/offers/items/${node.id}/duplicate`);
      toast.success(t('common.saved'));
      load();
    } catch { toast.error(t('common.error')); }
  };

  const handleMove = async (draggedId, targetId, position, variantId = null) => {
    try {
      const target = items.find(i => i.id === targetId);
      if (!target) return;
      let newParentId, newPosition;
      if (position === 'inside') {
        newParentId = targetId;
        newPosition = items.filter(i => i.parent_id === targetId).length;
      } else {
        newParentId = target.parent_id || null;
        const siblings = items.filter(i => (i.parent_id || null) === (target.parent_id || null)).sort((a, b) => a.display_order - b.display_order);
        newPosition = siblings.findIndex(s => s.id === targetId) + (position === 'after' ? 1 : 0);
      }
      const body = { parent_id: newParentId, position: newPosition };
      if (variantId) body.variant_id = variantId;
      await api.put(`/offers/items/${draggedId}/move`, body);
      load();
    } catch { toast.error(t('common.error')); }
  };

  const handleSaveItem = async (data) => {
    try {
      if (editingItem) await api.put(`/offers/items/${editingItem.id}`, data);
      else await api.post(`/offers/${offer.id}/items`, { ...data, parent_id: addParentId, item_type: addItemType, variant_id: addVariantId });
      setShowItemForm(false); setEditingItem(null); load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleSpreadsheetSave = async (itemId, spreadsheet, resultValue) => {
    try {
      const res = await api.put(`/offers/items/${itemId}/spreadsheet`, { spreadsheet, result_value: resultValue });
      if (res.data.tree_values) {
        const valMap = {};
        res.data.tree_values.forEach(v => { valMap[v.id] = parseFloat(v.result_value) || 0; });
        setItems(prev => prev.map(i => ({
          ...i,
          spreadsheet: i.id === itemId ? spreadsheet : i.spreadsheet,
          result_value: valMap[i.id] !== undefined ? valMap[i.id] : i.result_value,
        })));
      } else {
        setItems(prev => prev.map(i => i.id === itemId ? { ...i, spreadsheet, result_value: resultValue } : i));
      }
    } catch { toast.error(t('common.error')); }
  };

  const handleDeleteItem = async () => {
    try { await api.delete(`/offers/items/${deleteTarget.id}`); setDeleteTarget(null); load(); }
    catch { toast.error(t('common.error')); }
  };

  const handleStatusChange = async (status) => {
    try { await api.put(`/offers/${offer.id}`, { status }); setOffer(o => ({ ...o, status })); }
    catch { toast.error(t('common.error')); }
  };

  const handleCopyFromPricelist = async (pricelistItemId, targetParentId, sharedId, variantId = null) => {
    try {
      if (sharedId) {
        await api.post(`/offers/${offer.id}/copy-from-shared`, {
          share_id: sharedId, source_item_id: pricelistItemId, target_parent_id: targetParentId || null, variant_id: variantId
        });
      } else {
        await api.post(`/offers/${offer.id}/copy-from-pricelist`, {
          source_item_id: pricelistItemId, target_parent_id: targetParentId || null, variant_id: variantId
        });
      }
      toast.success(t('common.saved'));
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const [addVariantId, setAddVariantId] = useState(null);
  const openAddForm = (parentId, type, variantId = null) => { setAddParentId(parentId); setAddItemType(type); setAddVariantId(variantId); setEditingItem(null); setShowItemForm(true); };

  const generatePDF = (maxDepth) => {
    // Build sections per visible variant
    const visibleVariants = variants.filter(v => v.visible !== false);
    const allRows = [];

    for (const variant of visibleVariants) {
      const variantItems = items.filter(i => i.variant_id === variant.id);
      const variantTree = buildTree(variantItems);
      const rows = [];
      const collectRows = (nodes, depth = 0, parentHidden = false) => {
        for (const n of nodes) {
          if (depth >= maxDepth) continue;
          const hidden = parentHidden || n.visible === false;
          if (hidden) continue; // skip invisible nodes and all their children
          const isGroup = n.item_type === 'group';
          const val = Number(n.result_value || 0);
          if (n.children?.length && depth + 1 < maxDepth) {
            collectRows(n.children, depth + 1, false);
          }
          rows.push({ name: n.name, description: n.description || '', value: val, depth, isGroup, hasChildren: n.children?.length > 0 });
        }
      };
      collectRows(variantTree);
      if (rows.length > 0) {
        const vTotal = variantItems.filter(i => !i.parent_id).reduce((s, i) => s + Number(i.result_value || 0), 0);
        allRows.push({ variantName: visibleVariants.length > 1 ? variant.name : null, rows, total: vTotal });
      }
    }

    const company = offer.company_name || offer.client_company || '';
    const fmt2 = v => v.toLocaleString('pl-PL', {minimumFractionDigits:2});

    const issuer = offer.issuer_name || '';
    const issuerAddress = [offer.issuer_street, offer.issuer_zip, offer.issuer_city].filter(Boolean).join(', ');
    const priceType = offer.price_type || 'netto';
    const vatRate = Number(offer.vat_rate ?? 23);
    const cur = offer.currency || 'PLN';

    const printHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Oferta ${offer.offer_number}</title>
      <style>
        @page{margin:0;size:A4}
        body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:15mm;color:#1f2937;font-size:13px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .top-bar{background:linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%);color:white;margin:-15mm -15mm 0 -15mm;padding:18px 15mm;display:flex;justify-content:space-between;align-items:center}
        .top-bar h1{font-size:22px;margin:0;color:white;letter-spacing:0.5px}
        .top-bar .number{font-size:12px;color:rgba(255,255,255,0.75)}
        .top-bar .date{font-size:12px;color:rgba(255,255,255,0.75);text-align:right}
        .parties{display:flex;gap:24px;margin:24px 0 20px 0}
        .party{flex:1;border-radius:8px;padding:16px 18px}
        .party-issuer{background:#f0f4ff;border:1px solid #c7d2fe}
        .party-client{background:#f8fafc;border:1px solid #e2e8f0}
        .party-label{font-size:10px;text-transform:uppercase;letter-spacing:0.8px;font-weight:700;margin-bottom:8px}
        .party-issuer .party-label{color:#3b82f6}
        .party-client .party-label{color:#64748b}
        .party-name{font-size:15px;font-weight:700;color:#1e293b;margin-bottom:4px}
        .party-detail{font-size:13px;color:#475569;line-height:1.6}
        .meta{display:flex;gap:20px;margin-bottom:20px}
        .meta-item{flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 14px}
        .meta-label{font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#94a3b8;font-weight:600;margin-bottom:3px}
        .meta-value{font-size:14px;font-weight:600;color:#1e293b}
        .offer-title{background:#f0f4ff;border-left:4px solid #2563eb;padding:14px 18px;margin:0 0 20px 0;border-radius:0 8px 8px 0}
        .offer-title h2{margin:0;font-size:17px;color:#1e3a5f;font-weight:700}
        .offer-title .subtitle{font-size:12px;color:#64748b;margin-top:3px}
        table{width:100%;border-collapse:separate;border-spacing:0;margin-bottom:20px;border-radius:8px;overflow:hidden;border:1px solid #e2e8f0}
        th{background:#1e3a5f;color:white;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;padding:10px 12px}
        td{padding:8px 12px;border-bottom:1px solid #f1f5f9}
        .row-group{background:#eef2ff !important}
        .row-group td{font-weight:600;color:#1e3a5f;border-top:2px solid #c7d2fe}
        .row-item{background:#fff}
        .total-row{background:linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)}
        .total-row td{padding:12px;font-size:16px;font-weight:700;color:white;border:none}
        .notes{background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:16px;font-size:12px;margin-bottom:20px}
        .notes strong{color:#92400e}
        .notes p{margin:6px 0 0 0;color:#78350f}
        .desc{font-size:11px;font-weight:400;color:#64748b;margin-top:2px}
        a{color:#2563eb;text-decoration:none} a:hover{text-decoration:underline}
      </style></head><body>
      <div class="top-bar">
        <div style="text-align:left">
          <div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;letter-spacing:0.5px">Data wystawienia</div>
          <div style="font-size:16px;font-weight:700;color:white">${new Date(offer.created_at).toLocaleDateString('pl-PL')}</div>
        </div>
        <div style="text-align:center">
          <h1 style="font-size:20px;margin:0;color:white">Oferta</h1>
          <div style="font-size:12px;color:rgba(255,255,255,0.7)">${offer.offer_number}</div>
        </div>
        <div style="text-align:right">
          ${offer.issuer_logo ? '<img src="/uploads/' + offer.issuer_logo + '" style="height:36px;max-width:120px;object-fit:contain" />' : '<div style="font-size:14px;font-weight:700;color:white">' + (offer.issuer_name || '') + '</div>'}
        </div>
      </div>

      <div class="parties">
        <div class="party party-issuer">
          <div class="party-label">Wystawca</div>
          <div class="party-name">${issuer}</div>
          <div class="party-detail">
            ${offer.issuer_nip ? 'NIP: ' + offer.issuer_nip + '<br>' : ''}
            ${issuerAddress ? issuerAddress + '<br>' : ''}
            ${offer.issuer_phone ? '<a href="tel:' + offer.issuer_phone + '" style="color:#2563eb">tel. ' + offer.issuer_phone + '</a><br>' : ''}
            ${offer.issuer_email ? '<a href="mailto:' + offer.issuer_email + '" style="color:#2563eb">' + offer.issuer_email + '</a><br>' : ''}
            ${offer.issuer_website ? '<a href="' + (offer.issuer_website.startsWith('http') ? offer.issuer_website : 'https://' + offer.issuer_website) + '" style="color:#2563eb">' + offer.issuer_website + '</a><br>' : ''}
            ${offer.issuer_contact ? 'Osoba: ' + offer.issuer_contact : ''}
          </div>
        </div>
        <div class="party party-client">
          <div class="party-label">Klient</div>
          <div class="party-name">${company}</div>
          <div class="party-detail">
            ${offer.client_name ? offer.client_name + '<br>' : ''}
            ${offer.company_nip ? 'NIP: ' + offer.company_nip + '<br>' : ''}
            ${offer.company_address ? offer.company_address + '<br>' : ''}
            ${offer.company_email ? '<a href="mailto:' + offer.company_email + '" style="color:#2563eb">' + offer.company_email + '</a><br>' : ''}
            ${offer.company_phone ? '<a href="tel:' + offer.company_phone + '" style="color:#2563eb">tel. ' + offer.company_phone + '</a><br>' : ''}
            ${offer.address_montage ? '<strong style="color:#1e3a5f">Adres realizacji:</strong> ' + offer.address_montage : ''}
          </div>
        </div>
      </div>

      <div class="meta">
        <div class="meta-item">
          <div class="meta-label">Termin realizacji</div>
          <div class="meta-value">${offer.deadline ? new Date(offer.deadline).toLocaleDateString('pl-PL') : '—'}</div>
        </div>
        <div class="meta-item">
          <div class="meta-label">Ważność oferty</div>
          <div class="meta-value">${offer.valid_until ? new Date(offer.valid_until).toLocaleDateString('pl-PL') : '—'}</div>
        </div>
        <div class="meta-item">
          <div class="meta-label">${collaborators.length > 0 ? 'Autorzy oferty' : 'Autor oferty'}</div>
          ${(() => {
            const authors = [
              { name: offer.creator_name, job_title: offer.creator_job_title || '', email: offer.creator_contact_email || offer.creator_email, phone_mobile: offer.creator_phone_mobile, phone_office: offer.creator_phone_office },
              ...collaborators.map(c => ({ name: c.name, job_title: c.job_title || '', email: c.contact_email || c.email, phone_mobile: c.phone_mobile, phone_office: c.phone_office }))
            ];
            return authors.map((a, i) => `
              <div style="${i > 0 ? 'margin-top:8px;padding-top:8px;border-top:1px solid #e2e8f0' : ''}">
                <div class="meta-value">${a.name}${a.job_title ? ' <span style="font-weight:400;color:#64748b;font-size:12px">— ' + a.job_title + '</span>' : ''}</div>
                <div style="font-size:12px;color:#475569;line-height:1.5">
                  ${a.email ? '<a href="mailto:' + a.email + '" style="color:#2563eb">' + a.email + '</a><br>' : ''}
                  ${a.phone_mobile ? '<a href="tel:' + a.phone_mobile + '" style="color:#2563eb">kom. ' + a.phone_mobile + '</a><br>' : ''}
                  ${a.phone_office ? '<a href="tel:' + a.phone_office + '" style="color:#2563eb">tel. ' + a.phone_office + '</a>' : ''}
                </div>
              </div>
            `).join('');
          })()}
        </div>
      </div>

      <div class="offer-title">
        <h2>${offer.name}</h2>
        ${offer.description ? '<div class="subtitle">' + offer.description + '</div>' : ''}
      </div>

      ${(variables.local || []).length > 0 ? `
      <div style="background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;padding:14px 18px;margin-bottom:20px">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#92400e;font-weight:600;margin-bottom:8px">Parametry konfiguracji</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 24px">
          ${(variables.local || []).map(v => `
            <div style="display:flex;justify-content:space-between;font-size:13px;padding:2px 0">
              <span style="color:#6b7280">${v.description || v.key}</span>
              <span style="font-weight:600;color:#1f2937;font-family:monospace">${Number(v.value).toLocaleString('pl-PL')}</span>
            </div>`).join('')}
        </div>
      </div>` : ''}

      ${allRows.map(section => {
        let vNetto, vBrutto, vVat;
        if (priceType === 'netto') {
          vNetto = section.total; vVat = section.total * vatRate / 100; vBrutto = section.total + vVat;
        } else {
          vBrutto = section.total; vVat = section.total - section.total / (1 + vatRate / 100); vNetto = section.total - vVat;
        }
        return `
      ${section.variantName ? '<h3 style="font-size:15px;font-weight:700;color:#1e3a5f;margin:24px 0 8px 0">' + section.variantName + '</h3>' : ''}
      <table>
        <thead><tr><th>Pozycja</th><th style="text-align:right;width:140px">Wartość ${priceType === 'netto' ? 'netto' : 'brutto'} (${cur})</th></tr></thead>
        <tbody>
        ${section.rows.map(r => {
          const indent = r.depth * 20;
          const bold = r.isGroup || r.hasChildren;
          const rowClass = r.isGroup ? 'row-group' : 'row-item';
          return '<tr class="' + rowClass + '"><td style="padding-left:' + (indent + 12) + 'px;font-weight:' + (bold ? '600' : '400') + '">' + r.name + (r.description ? '<div class="desc">' + r.description + '</div>' : '') + '</td><td style="text-align:right;font-weight:' + (bold ? '700' : '400') + ';color:' + (bold ? '#1e3a5f' : '#374151') + '">' + (r.value > 0 ? fmt2(r.value) : '') + '</td></tr>';
        }).join('')}
        ${priceType === 'netto' ? `
        <tr style="background:#f8fafc"><td style="text-align:right;padding:8px 12px;font-weight:600;color:#475569;border-bottom:1px solid #e2e8f0">Razem netto</td><td style="text-align:right;padding:8px 12px;font-weight:600;color:#475569;border-bottom:1px solid #e2e8f0">${fmt2(vNetto)} ${cur}</td></tr>
        <tr style="background:#f8fafc"><td style="text-align:right;padding:8px 12px;color:#64748b;border-bottom:1px solid #e2e8f0">VAT ${vatRate}%</td><td style="text-align:right;padding:8px 12px;color:#64748b;border-bottom:1px solid #e2e8f0">${fmt2(vVat)} ${cur}</td></tr>
        <tr class="total-row"><td style="text-align:right">RAZEM BRUTTO</td><td style="text-align:right">${fmt2(vBrutto)} ${cur}</td></tr>
        ` : `
        <tr class="total-row"><td style="text-align:right">RAZEM BRUTTO</td><td style="text-align:right">${fmt2(vBrutto)} ${cur}</td></tr>
        <tr style="background:#f8fafc"><td style="text-align:right;padding:8px 12px;color:#64748b;border-top:1px solid #e2e8f0">w tym VAT ${vatRate}%</td><td style="text-align:right;padding:8px 12px;color:#64748b;border-top:1px solid #e2e8f0">${fmt2(vVat)} ${cur}</td></tr>
        <tr style="background:#f8fafc"><td style="text-align:right;padding:8px 12px;font-weight:600;color:#475569">Wartość netto</td><td style="text-align:right;padding:8px 12px;font-weight:600;color:#475569">${fmt2(vNetto)} ${cur}</td></tr>
        `}
        </tbody>
      </table>`;
      }).join('')}
      ${(offer.extra_notes_items && offer.extra_notes_items.length > 0) ?
        '<div class="notes"><strong>Dodatkowe informacje:</strong>' +
        offer.extra_notes_items.map(n => '<p><strong>' + n.title + ':</strong> ' + n.content + '</p>').join('') +
        '</div>' : (offer.extra_notes ? '<div class="notes"><strong>Dodatkowe informacje:</strong><br>' + offer.extra_notes + '</div>' : '')}
      </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(printHtml);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const tree = buildTree(items);
  const s = STATUS[offer.status] || STATUS.draft;

  // Handle drop from pricelist panel onto offer tree area
  const handleOfferDrop = (e, variantId = null) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const raw = e.dataTransfer.getData('application/x-pricelist-to-offer');
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.itemId) handleCopyFromPricelist(data.itemId, null, data.sharedId, variantId);
    } catch (err) { console.error('Drop error:', err); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 mb-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2">
          <ArrowLeft size={16} /> {t('offers.backToList')}
        </button>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-start justify-between">
            {/* Lewa kolumna — dane oferty */}
            <div className="space-y-0.5 text-sm text-gray-600">
              <div><span className="text-gray-400">{t('offers.offerNumber')}</span> <span className="font-mono font-semibold text-purple-600">{offer.offer_number}</span></div>
              {!headerCollapsed && (
                <div><span className="text-gray-400">{t('offers.issueDate')}</span> <span className="font-medium">{new Date(offer.created_at).toLocaleDateString('pl-PL')}</span></div>
              )}
              {(offer.company_name || offer.client_company) && (
                <div><span className="text-gray-400">{t('offers.forCompany')}</span> <span className="font-bold text-gray-900">{offer.company_name || offer.client_company}</span></div>
              )}
              {!headerCollapsed && (
                <div><span className="text-gray-400">{t('offers.preparation')}</span> <span className="font-medium">{[offer.creator_name, ...collaborators.map(c => c.name)].join(', ')}</span></div>
              )}
            </div>
            {/* Środkowa kolumna — suma, koszty, profit */}
            <div className="flex items-center gap-6 px-4">
              <div className="text-center">
                <div className="text-xs text-gray-400 uppercase">{t('offers.sum')}</div>
                <div className="font-bold text-lg text-gray-800">
                  {Number(offer.total_value || 0).toLocaleString('pl-PL', {minimumFractionDigits:2})} {offer.currency || 'PLN'}
                </div>
                <div className="text-xs text-gray-400">{offer.price_type === 'brutto' ? t('offers.brutto') : t('offers.netto')}</div>
              </div>
              {(() => {
                // Sum purchase_cost and profit_value from root items
                const totalCost = items.filter(i => !i.parent_id).reduce((sum, i) => sum + (parseFloat(i.purchase_cost) || 0), 0);
                const totalSell = Number(offer.total_value || 0);
                const profit = items.filter(i => !i.parent_id).reduce((sum, i) => sum + (parseFloat(i.profit_value) || 0), 0);
                return (<>
                  <div className="text-center">
                    <div className="text-xs text-gray-400 uppercase">{t('offers.costs')}</div>
                    <div className={`font-semibold text-sm ${totalCost > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {totalCost > 0 ? totalCost.toLocaleString('pl-PL', {minimumFractionDigits:2}) : '—'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400 uppercase">{t('offers.profit')}</div>
                    <div className={`font-semibold text-sm ${profit > 0 ? 'text-green-600' : profit < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {totalCost > 0 ? profit.toLocaleString('pl-PL', {minimumFractionDigits:2}) : '—'}
                    </div>
                  </div>
                </>);
              })()}
            </div>
            {/* Prawa kolumna — przyciski */}
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <button onClick={async () => {
                  try {
                    const r = await api.post(`/offers/${offer.id}/recalculate`);
                    setItems(r.data.items);
                    setOffer(o => ({ ...o, total_value: r.data.total }));
                    toast.success('Przeliczono');
                  } catch { toast.error(t('common.error')); }
                }} className="flex items-center gap-1 px-3 py-1.5 border border-green-300 text-green-600 hover:bg-green-50 rounded text-sm" title="Przelicz ofertę">
                  <RefreshCw size={14} />
                </button>
                <button onClick={() => setShowOfferDetails(true)} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded text-sm">
                  <Pencil size={14} /> {t('offers.detailsButton')}
                </button>
                <button onClick={() => setShowShareDialog(true)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium">
                  <Link2 size={14} /> {t('offers.shareButton')}
                </button>
                <button onClick={() => setShowPdfDialog(true)} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium">
                  <FileDown size={14} /> {t('offers.pdfButton')}
                </button>
                {offer.status === 'accepted' && (
                  <button onClick={() => setShowNprDialog(true)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium">
                    <Send size={14} /> NPR
                  </button>
                )}
                {currentUser?.role === 'admin' && (
                  <button onClick={() => setShowHistory(true)} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-500 hover:bg-gray-50 rounded text-sm">
                    <History size={14} /> {t('offers.historyButton')}
                  </button>
                )}
              </div>
            </div>
          </div>
          {!headerCollapsed && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
              <EditableTitle value={offer.name} onSave={(name) => saveOfferField({ name })} />
              <select value={offer.status} onChange={e => handleStatusChange(e.target.value)}
                className={`px-2 py-1.5 border rounded text-sm font-medium flex-shrink-0 ${s.color}`}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{t(v.labelKey)}</option>)}
              </select>
            </div>
          )}
          <button onClick={() => setHeaderCollapsed(!headerCollapsed)}
            className="w-full flex items-center justify-center pt-1 -mb-1 text-gray-300 hover:text-gray-500">
            {headerCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {/* View mode toggle */}
      <div className="flex items-center gap-1 mb-2">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setViewMode('items')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'items' ? 'bg-white shadow text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}>
            <Package size={14} className="inline mr-1" />Pozycje
          </button>
          <button onClick={() => setViewMode('content')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'content' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            <FileText size={14} className="inline mr-1" />Treść oferty
          </button>
        </div>
        {offer.content_md && viewMode === 'items' && <span className="text-xs text-blue-500 ml-2">Treść MD zapisana</span>}
      </div>

      {viewMode === 'content' ? (
        <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden bg-white">
          <OfferContentEditor offer={offer} onSave={(md) => setOffer(o => ({...o, content_md: md}))} />
        </div>
      ) : (
      <SplitPanels
        left={
          <div className="flex flex-col overflow-hidden gap-2">
            {/* Configurator panel for offers with local variables */}
            {offerHasLocalVars && (
              <div className="border border-amber-200 rounded-lg bg-amber-50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Variable size={14} className="text-amber-600" />
                  <span className="text-xs font-semibold text-amber-700">Konfiguracja</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {(variables.local || []).map(v => (
                    <div key={v.key} className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-36 truncate" title={`${v.key} — ${v.description || ''}`}>
                        {v.description || v.key}
                      </label>
                      <input type="number" value={v.value}
                        onChange={e => setVariables(prev => ({ ...prev, local: (prev.local || []).map(lv => lv.key === v.key ? { ...lv, value: e.target.value } : lv) }))}
                        onBlur={e => handleUpdateOfferVar(v.key, e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                        className="w-20 px-1.5 py-0.5 border border-amber-300 rounded text-xs text-right font-mono bg-white focus:ring-1 focus:ring-amber-400" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {loading ? <div className="p-8 text-gray-400 text-xs">{t('common.loading')}</div> :
              variants.map(variant => {
                const variantItems = items.filter(i => i.variant_id === variant.id);
                const variantTree = buildTree(variantItems);
                const variantTotal = variantItems.filter(i => !i.parent_id).reduce((s, i) => s + Number(i.result_value || 0), 0);
                return (
                  <div key={variant.id} className="border border-gray-200 rounded-lg bg-white flex flex-col"
                    onDragOver={e => e.preventDefault()} onDrop={e => {
                      // Handle internal offer item move between variants
                      const offerData = e.dataTransfer.getData('application/x-offer-item');
                      if (offerData) {
                        e.preventDefault(); e.stopPropagation();
                        try {
                          const data = JSON.parse(offerData);
                          if (data.id) {
                            const draggedItem = items.find(i => i.id === data.id);
                            if (draggedItem && draggedItem.variant_id !== variant.id) {
                              // Move to root of target variant
                              (async () => {
                                try {
                                  await api.put(`/offers/items/${data.id}/move`, { parent_id: null, position: variantItems.filter(i => !i.parent_id).length, variant_id: variant.id });
                                  load();
                                } catch { toast.error(t('common.error')); }
                              })();
                            }
                          }
                        } catch {}
                        return;
                      }
                      handleOfferDrop(e, variant.id);
                    }}>
                    <div className="px-3 py-2 bg-purple-50 border-b border-purple-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <input type="text" value={variant.name} className="text-sm font-bold text-purple-700 bg-transparent border-none outline-none w-40"
                          onChange={e => {
                            const newName = e.target.value;
                            setVariants(vs => vs.map(v => v.id === variant.id ? {...v, name: newName} : v));
                          }}
                          onBlur={e => { api.put(`/offers/${offer.id}/variants/${variant.id}`, { name: e.target.value }).catch(() => {}); }}
                        />
                        <span className="text-sm text-purple-500 font-bold">{variantTotal > 0 ? variantTotal.toLocaleString('pl-PL', {minimumFractionDigits:2}) + ' ' + (offer.currency||'PLN') : ''}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={async () => {
                          const newVal = !variant.visible;
                          setVariants(vs => vs.map(v => v.id === variant.id ? {...v, visible: newVal} : v));
                          api.put(`/offers/${offer.id}/variants/${variant.id}`, { visible: newVal }).catch(() => {});
                        }} className={`p-1 rounded ${variant.visible !== false ? 'text-gray-400 hover:bg-gray-100' : 'text-red-400 hover:bg-red-50'}`}
                          title={variant.visible !== false ? t('offers.visibleForClient') : t('offers.hiddenForClient')}>
                          {variant.visible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
                        </button>
                        {canCreate && <>
                          <button onClick={() => openAddForm(null, 'group', variant.id)} className="p-1 text-amber-600 hover:bg-amber-100 rounded" title="Nowa grupa"><FolderOpen size={14} /></button>
                          <button onClick={() => openAddForm(null, 'item', variant.id)} className="p-1 text-purple-600 hover:bg-purple-100 rounded" title="Nowa pozycja"><Plus size={14} /></button>
                          <button onClick={async () => {
                            try { await api.post(`/offers/${offer.id}/variants`, { name: variant.name + ' (kopia)', source_variant_id: variant.id }); load(); }
                            catch { toast.error(t('common.error')); }
                          }} className="p-1 text-blue-500 hover:bg-blue-50 rounded" title="Kopiuj drzewo"><Copy size={14} /></button>
                        </>}
                        {canDelete && variants.length > 1 && (
                          <button onClick={() => {
                            if (window.confirm(`Usunąć "${variant.name}"?`)) {
                              api.delete(`/offers/${offer.id}/variants/${variant.id}`).then(() => load()).catch(() => toast.error(t('common.error')));
                            }
                          }} className="p-1 text-red-400 hover:bg-red-50 rounded" title="Usuń drzewo"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </div>
                    <div className="p-2">
                      {variantItems.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-xs">
                          <Package size={24} className="mx-auto mb-1 opacity-50" /><p>{t('offers.emptyVariant')}</p>
                          <p className="mt-1 text-gray-300">{t('offers.dragFromPricelist')}</p>
                        </div>
                      ) : offerHasLocalVars ? (
                        /* Configurator mode: show only group names + totals, no full tree */
                        variantTree.map(node => (
                          <div key={node.id} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-gray-50 text-xs">
                            <div className="flex items-center gap-1.5">
                              {node.item_type === 'group' ? <FolderOpen size={14} className="text-amber-500" /> : <Package size={14} className="text-gray-400" />}
                              <span className="text-gray-700 font-medium">{node.name}</span>
                            </div>
                            <span className="text-sm font-semibold text-emerald-700 tabular-nums">
                              {Number(node.result_value || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        ))
                      ) : (
                        variantTree.map(node => (
                          <TreeNode key={node.id} node={node} depth={0}
                            canEdit={canEdit} canDelete={canDelete} canCreate={canCreate}
                            onEdit={item => { setEditingItem(item); setAddItemType(item.item_type); setShowItemForm(true); }}
                            onDelete={setDeleteTarget} onAdd={(parentId, type) => openAddForm(parentId, type, variant.id)}
                            onMove={(draggedId, targetId, position) => handleMove(draggedId, targetId, position, variant.id)}
                            onSpreadsheetSave={handleSpreadsheetSave} activeSpreadsheet={activeSpreadsheet}
                            setActiveSpreadsheet={setActiveSpreadsheet} variables={variables}
                            onDuplicate={handleDuplicate}
                            onCopyFromPricelist={handleCopyFromPricelist}
                            onToggleVisible={async (item) => {
                              const newVal = item.visible === false ? true : false;
                              setItems(prev => prev.map(i => i.id === item.id ? {...i, visible: newVal} : i));
                              api.put(`/offers/items/${item.id}`, { visible: newVal }).catch(() => {});
                            }} />
                        ))
                      )}
                    </div>
                    <VariantConversation offerId={offer.id} variantId={variant.id} variantName={variant.name} />
                  </div>
                );
              })
            }
            {/* Add variant button */}
            {canCreate && (
              <div className="flex items-center gap-1">
                <button onClick={async () => {
                  try { await api.post(`/offers/${offer.id}/variants`, {}); load(); }
                  catch { toast.error(t('common.error')); }
                }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 border-2 border-dashed border-purple-300 text-purple-500 hover:bg-purple-50 rounded-lg text-xs font-medium">
                  <Plus size={14} /> {t('offers.newTree')}
                </button>
              </div>
            )}
          </div>
        }
        right={
          <PricelistReadPanel toast={toast} offerId={offer.id} onCopyToOffer={(itemId, sharedId) => {
            const selectedItem = activeSpreadsheet ? items.find(i => i.id === activeSpreadsheet) : null;
            const variantId = selectedItem?.variant_id || (variants.length > 0 ? variants[0].id : null);
            handleCopyFromPricelist(itemId, activeSpreadsheet, sharedId, variantId);
          }} />
        }
      />
      )}

      <Modal isOpen={showItemForm} onClose={() => { setShowItemForm(false); setEditingItem(null); }}
        title={editingItem ? t('common.edit') : (addItemType === 'group' ? 'Nowa grupa' : 'Nowa pozycja')}>
        <ItemForm initial={editingItem} isGroup={editingItem ? editingItem.item_type === 'group' : addItemType === 'group'}
          onSave={handleSaveItem} onCancel={() => { setShowItemForm(false); setEditingItem(null); }} />
      </Modal>
      <ConfirmDialog isOpen={!!deleteTarget} type="danger"
        title={t('common.delete')} message={`Usunąć "${deleteTarget?.name}"?`}
        onConfirm={handleDeleteItem} onCancel={() => setDeleteTarget(null)} />

      {/* PDF dialog */}
      <Modal isOpen={showPdfDialog} onClose={() => setShowPdfDialog(false)} title={t('offers.pdfButton')}>
        <PdfExportDialog maxTreeDepth={getMaxDepth(buildTree(items))} onExport={(depth) => { generatePDF(depth); setShowPdfDialog(false); }} onCancel={() => setShowPdfDialog(false)} />
      </Modal>

      {/* Share dialog */}
      <Modal isOpen={showShareDialog} onClose={() => setShowShareDialog(false)} title={t('offers.shareOfferTitle')} maxWidth="max-w-lg">
        <ShareOfferDialog offer={offer} onClose={() => setShowShareDialog(false)} />
      </Modal>

      {/* Offer details modal */}
      <Modal isOpen={showOfferDetails} onClose={() => setShowOfferDetails(false)} title={t('offers.detailsButton')} maxWidth="max-w-2xl">
        <OfferDetailsForm offer={offer} onSave={(data) => { saveOfferField(data); setShowOfferDetails(false); }} onCancel={() => setShowOfferDetails(false)} />
      </Modal>

      {/* History modal (admin) */}
      <Modal isOpen={showHistory} onClose={() => setShowHistory(false)} title={t('offers.historyTitle')} maxWidth="max-w-2xl">
        <OfferHistory offerId={offer.id} />
      </Modal>

      {/* NPR transfer modal */}
      <Modal isOpen={showNprDialog} onClose={() => setShowNprDialog(false)} title="Kopiuj do NPR" maxWidth="max-w-2xl">
        <SendToNprDialog
          offer={offer}
          items={items}
          sending={nprSending}
          onSend={async (payload) => {
            setNprSending(true);
            try {
              const r = await api.post('/integration/send-to-npr', {
                offer_id: offer.id,
                image_ids: payload.image_ids,
                include: payload.include,
              });
              toast.success(r.data.message);
              setShowNprDialog(false);
            } catch (err) {
              toast.error(err.response?.data?.error || 'Błąd wysyłki do NPR');
            } finally {
              setNprSending(false);
            }
          }}
          onCancel={() => setShowNprDialog(false)}
        />
      </Modal>
    </div>
  );
}

function getMaxDepth(tree, depth = 0) {
  let max = depth;
  for (const n of tree) {
    if (n.children?.length) max = Math.max(max, getMaxDepth(n.children, depth + 1));
  }
  return max + 1;
}

function SendToNprDialog({ offer, items, sending, onSend, onCancel }) {
  const [include, setInclude] = useState({
    company: true,
    description: true,
    items: true,
    extra_notes: true,
    address_montage: true,
    deadline: true,
    images: true,
  });
  const [attachments, setAttachments] = useState([]);
  const [entityImages, setEntityImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);

  useEffect(() => {
    // Load all available images for this offer
    const loadImages = async () => {
      try {
        const [convRes, imgRes] = await Promise.all([
          api.get(`/offers/${offer.id}/messages`).catch(() => ({ data: { messages: [] } })),
          api.get(`/images/offer/${offer.id}`).catch(() => ({ data: [] })),
        ]);
        const convAttachments = (convRes.data.messages || [])
          .filter(m => m.attachment_url)
          .map(m => ({ id: m.id, type: 'conversation', url: m.attachment_url, name: m.attachment_name || 'załącznik', thumbnail: m.attachment_url }));
        const entImgs = (Array.isArray(imgRes.data) ? imgRes.data : [])
          .map(i => ({ id: i.id, type: 'entity', url: i.url, name: i.url?.split('/').pop() || 'zdjęcie', thumbnail: i.thumbnail_url || i.url }));
        setAttachments(convAttachments);
        setEntityImages(entImgs);
        // Select all by default
        setSelectedImages([...convAttachments.map(a => a.id), ...entImgs.map(i => i.id)]);
      } catch {}
      finally { setLoadingImages(false); }
    };
    loadImages();
  }, [offer.id]);

  const allImages = [...attachments, ...entityImages];
  const toggleImage = (id) => setSelectedImages(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleAll = (key) => setInclude(prev => ({ ...prev, [key]: !prev[key] }));

  const itemTree = items.filter(i => !i.parent_id);
  const hasDescription = !!offer.description;
  const hasExtraNotes = !!(offer.extra_notes || offer.extra_notes_items);
  const hasAddress = !!offer.address_montage;
  const hasDeadline = !!offer.deadline;

  return (
    <div className="space-y-4">
      {/* What to export */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Elementy do eksportu</h3>
        <div className="space-y-2">
          {/* Company */}
          <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input type="checkbox" checked={include.company} onChange={() => toggleAll('company')} className="mt-0.5 w-4 h-4 text-emerald-600 rounded" />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-800">Dane firmy</span>
              <p className="text-xs text-gray-500">{offer.company_name || offer.client_company || '—'}{offer.company_nip ? ` (NIP: ${offer.company_nip})` : ''}</p>
            </div>
          </label>

          {/* Description */}
          {hasDescription && (
            <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={include.description} onChange={() => toggleAll('description')} className="mt-0.5 w-4 h-4 text-emerald-600 rounded" />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-800">Opis oferty</span>
                <p className="text-xs text-gray-500 line-clamp-2">{offer.description}</p>
              </div>
            </label>
          )}

          {/* Items */}
          {itemTree.length > 0 && (
            <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={include.items} onChange={() => toggleAll('items')} className="mt-0.5 w-4 h-4 text-emerald-600 rounded" />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-800">Pozycje oferty ({items.length})</span>
                <p className="text-xs text-gray-500">{itemTree.slice(0, 4).map(i => i.name).join(', ')}{itemTree.length > 4 ? ` (+${itemTree.length - 4})` : ''}</p>
              </div>
            </label>
          )}

          {/* Extra notes */}
          {hasExtraNotes && (
            <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={include.extra_notes} onChange={() => toggleAll('extra_notes')} className="mt-0.5 w-4 h-4 text-emerald-600 rounded" />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-800">Uwagi dodatkowe</span>
                <p className="text-xs text-gray-500 line-clamp-1">{offer.extra_notes || 'Uwagi w pozycjach'}</p>
              </div>
            </label>
          )}

          {/* Address */}
          {hasAddress && (
            <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={include.address_montage} onChange={() => toggleAll('address_montage')} className="mt-0.5 w-4 h-4 text-emerald-600 rounded" />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-800">Adres montażu</span>
                <p className="text-xs text-gray-500">{offer.address_montage}</p>
              </div>
            </label>
          )}

          {/* Deadline */}
          {hasDeadline && (
            <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={include.deadline} onChange={() => toggleAll('deadline')} className="mt-0.5 w-4 h-4 text-emerald-600 rounded" />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-800">Termin realizacji</span>
                <p className="text-xs text-gray-500">{new Date(offer.deadline).toLocaleDateString('pl-PL')}</p>
              </div>
            </label>
          )}
        </div>
      </div>

      {/* Images selection */}
      {allImages.length > 0 && (
        <div>
          <label className="flex items-center gap-3 mb-2 cursor-pointer">
            <input type="checkbox" checked={include.images} onChange={() => toggleAll('images')} className="w-4 h-4 text-emerald-600 rounded" />
            <span className="text-sm font-semibold text-gray-700">Zdjęcia i załączniki ({allImages.length})</span>
          </label>
          {include.images && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1">
              {allImages.map(img => {
                const isSelected = selectedImages.includes(img.id);
                return (
                  <div key={`${img.type}-${img.id}`}
                    onClick={() => toggleImage(img.id)}
                    className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-colors ${isSelected ? 'border-emerald-500 ring-1 ring-emerald-300' : 'border-gray-200 hover:border-gray-300'}`}>
                    <img src={img.thumbnail} alt={img.name}
                      className="w-full h-16 object-cover" onError={e => { e.target.style.display = 'none'; }} />
                    <div className={`absolute inset-0 flex items-center justify-center ${isSelected ? 'bg-emerald-500/20' : 'bg-black/0 hover:bg-black/10'}`}>
                      {isSelected && <CheckCircle size={20} className="text-emerald-600 bg-white rounded-full" />}
                    </div>
                    <div className="text-xs text-gray-500 px-1 py-0.5 truncate bg-white">{img.name}</div>
                  </div>
                );
              })}
            </div>
          )}
          {loadingImages && <p className="text-xs text-gray-400">Ładowanie zdjęć...</p>}
        </div>
      )}

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        <strong>Uwaga:</strong> Ceny, wartości i dane finansowe <strong>nie będą</strong> przesyłane. Do NPR trafi zlecenie montażowe.
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-2 border-t">
        <span className="text-xs text-gray-400">
          {Object.values(include).filter(Boolean).length} elementów{include.images ? `, ${selectedImages.length} zdjęć` : ''}
        </span>
        <div className="flex gap-2">
          <button onClick={onCancel} disabled={sending} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Anuluj
          </button>
          <button onClick={() => onSend({ image_ids: include.images ? selectedImages : [], include })} disabled={sending}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-medium">
            {sending ? <><Loader2 size={14} className="animate-spin" /> Wysyłam...</> : <><Send size={14} /> Kopiuj do NPR</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function PdfExportDialog({ maxTreeDepth, onExport, onCancel }) {
  const { t } = useTranslation();
  const [depth, setDepth] = useState(maxTreeDepth);
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Wybierz do jakiego poziomu wyświetlić wycenę w PDF. Pozycje szczegółowe będą na górze, podsumowania grup na dole.</p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Poziom szczegółowości</label>
        <input type="range" min={1} max={maxTreeDepth} value={depth} onChange={e => setDepth(parseInt(e.target.value))}
          className="w-full" />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>Tylko główne grupy</span>
          <span>Poziom: {depth} z {maxTreeDepth}</span>
          <span>Wszystkie szczegóły</span>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
        <button onClick={() => onExport(depth)} className="flex items-center gap-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm">
          <FileDown size={14} /> {t('offers.pdfButton')}
        </button>
      </div>
    </div>
  );
}

function OfferDetailsForm({ offer, onSave, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company_id: offer.company_id || '',
    client_name: offer.client_name || '', client_email: offer.client_email || '',
    client_phone: offer.client_phone || '', client_company: offer.client_company || '',
    company_name: offer.company_name || '', company_nip: offer.company_nip || '',
    company_address: offer.company_address || '', company_email: offer.company_email || '',
    company_phone: offer.company_phone || '',
    deadline: offer.deadline?.split('T')[0] || '', valid_until: offer.valid_until?.split('T')[0] || '',
    extra_notes: offer.extra_notes || '', extra_notes_items: offer.extra_notes_items || [],
    currency: offer.currency || 'PLN',
    address_montage: offer.address_montage || '',
    price_type: offer.price_type || 'netto',
    vat_rate: offer.vat_rate ?? 23,
  });
  const [companies, setCompanies] = useState([]);
  const [validityMode, setValidityMode] = useState('days');
  const [validityDays, setValidityDays] = useState(14);
  const [deadlineMode, setDeadlineMode] = useState('days');
  const [deadlineDays, setDeadlineDays] = useState(14);
  const [companySearch, setCompanySearch] = useState('');
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompanyMode, setNewCompanyMode] = useState('manual'); // manual | ai | gus
  const [aiText, setAiText] = useState('');
  const [gusNip, setGusNip] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', nip: '', regon: '', email: '', phone: '', website: '', address_street: '', address_city: '', address_zip: '', contact_person: '', address_montage: '' });

  const ic = "w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm";
  const lc = "block text-xs font-medium text-gray-600 mb-1";

  useEffect(() => {
    api.get('/system/companies').then(r => setCompanies(r.data.companies)).catch(() => {});
  }, []);

  const selectCompany = (company) => {
    setForm(f => ({
      ...f,
      company_id: company.id,
      company_name: company.name, company_nip: company.nip || '',
      company_address: [company.address_street, company.address_zip, company.address_city].filter(Boolean).join(', '),
      company_email: company.email || '', company_phone: company.phone || '',
      client_name: company.contact_person || f.client_name,
      address_montage: company.address_montage || '',
    }));
    setCompanySearch('');
  };

  const handleAddCompany = async () => {
    if (!newCompany.name) return;
    try {
      const r = await api.post('/system/companies', newCompany);
      setCompanies(prev => [...prev, r.data.company]);
      selectCompany(r.data.company);
      setShowNewCompany(false);
      setNewCompany({ name: '', nip: '', regon: '', email: '', phone: '', website: '', address_street: '', address_city: '', address_zip: '', contact_person: '', address_montage: '' });
      setNewCompanyMode('manual');
    } catch {}
  };

  const filteredCompanies = companies.filter(c =>
    !companySearch || c.name.toLowerCase().includes(companySearch.toLowerCase()) ||
    (c.nip && c.nip.includes(companySearch)) ||
    (c.contact_person && c.contact_person.toLowerCase().includes(companySearch.toLowerCase()))
  );

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      {/* Firma — picker */}
      <div className="border-b pb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Firma</h3>
        {form.company_name ? (
          <div className="bg-gray-50 border rounded-lg p-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-800">{form.company_name}</div>
              <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5">
                {form.company_nip && <span>NIP: {form.company_nip}</span>}
                {form.company_email && <span>{form.company_email}</span>}
                {form.company_phone && <span>{form.company_phone}</span>}
              </div>
              {form.company_address && <div className="text-xs text-gray-400 mt-0.5">{form.company_address}</div>}
            </div>
            <button type="button" onClick={() => setForm(f => ({...f, company_id: '', company_name: '', company_nip: '', company_address: '', company_email: '', company_phone: ''}))}
              className="text-xs text-red-500 hover:underline">{t('common.edit')}</button>
          </div>
        ) : (
          <div>
            <div className="flex gap-2 mb-2">
              <input type="text" value={companySearch} onChange={e => setCompanySearch(e.target.value)}
                className={ic} placeholder="Wyszukaj firmę (nazwa, NIP)..." />
              <button type="button" onClick={() => setShowNewCompany(!showNewCompany)}
                className="px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg text-xs flex-shrink-0 whitespace-nowrap">
                + Nowa
              </button>
            </div>
            {companySearch && (
              <div className="border rounded-lg max-h-32 overflow-auto mb-2">
                {filteredCompanies.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-400">{t('common.noResults')}</div>
                ) : filteredCompanies.map(c => (
                  <button key={c.id} type="button" onClick={() => selectCompany(c)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b last:border-0">
                    <span className="font-medium text-gray-800">{c.name}</span>
                    {c.nip && <span className="text-gray-400 ml-2">NIP: {c.nip}</span>}
                    {c.contact_person && <span className="text-gray-400 ml-2">{c.contact_person}</span>}
                  </button>
                ))}
              </div>
            )}
            {showNewCompany && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2 mb-2">
                {/* Mode tabs */}
                <div className="flex gap-1 mb-2">
                  {[{k:'manual',l:'Ręcznie'},{k:'ai',l:'Z tekstu (AI)'},{k:'gus',l:'Po NIP (GUS)'}].map(t => (
                    <button key={t.k} type="button" onClick={() => setNewCompanyMode(t.k)}
                      className={`px-2 py-1 rounded text-xs font-medium ${newCompanyMode === t.k ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      {t.l}
                    </button>
                  ))}
                </div>

                {/* AI mode */}
                {newCompanyMode === 'ai' && (
                  <div className="space-y-2">
                    <textarea value={aiText} onChange={e => setAiText(e.target.value)} className={ic} rows={4}
                      placeholder="Wklej tekst z danymi firmy (podpis email, wizytówka, stopka, notatka)..." autoFocus />
                    <button type="button" disabled={!aiText.trim() || aiLoading} onClick={async () => {
                      setAiLoading(true);
                      try {
                        const r = await api.post('/ai/parse-company', { text: aiText });
                        if (r.data.company) {
                          setNewCompany(prev => ({...prev, ...Object.fromEntries(Object.entries(r.data.company).filter(([,v]) => v))}));
                          setNewCompanyMode('manual');
                        }
                      } catch (err) { toast.error(err.response?.data?.error || 'Błąd AI'); }
                      finally { setAiLoading(false); }
                    }} className="px-3 py-1.5 bg-purple-600 disabled:opacity-40 text-white rounded text-xs flex items-center gap-1">
                      {aiLoading ? <><Loader2 size={12} className="animate-spin" /> Analizuję...</> : 'Rozpoznaj dane'}
                    </button>
                  </div>
                )}

                {/* GUS mode */}
                {newCompanyMode === 'gus' && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input type="text" value={gusNip} onChange={e => setGusNip(e.target.value)} className={ic}
                        placeholder="Wpisz NIP (10 cyfr)" autoFocus />
                      <button type="button" disabled={!gusNip.replace(/\D/g, '').length || aiLoading} onClick={async () => {
                        setAiLoading(true);
                        try {
                          const r = await api.post('/ai/gus-lookup', { nip: gusNip });
                          if (r.data.company) {
                            setNewCompany(prev => ({...prev, ...Object.fromEntries(Object.entries(r.data.company).filter(([k,v]) => v && k !== 'found'))}));
                            setNewCompanyMode('manual');
                          } else { toast.error('Nie znaleziono firmy w rejestrze'); }
                        } catch (err) { toast.error(err.response?.data?.error || 'Błąd wyszukiwania'); }
                        finally { setAiLoading(false); }
                      }} className="px-3 py-1.5 bg-blue-600 disabled:opacity-40 text-white rounded text-xs flex-shrink-0 flex items-center gap-1 whitespace-nowrap">
                        {aiLoading ? <><Loader2 size={12} className="animate-spin" /> Szukam...</> : 'Pobierz z GUS'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Manual mode (also shown after AI/GUS fill) */}
                {newCompanyMode === 'manual' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className={lc}>Nazwa firmy *</label><input type="text" value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} className={ic} autoFocus /></div>
                      <div><label className={lc}>Krótka nazwa</label><input type="text" value={newCompany.short_name||''} onChange={e => setNewCompany({...newCompany, short_name: e.target.value})} className={ic} /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><label className={lc}>{t('common.nip')}</label><input type="text" value={newCompany.nip} onChange={e => setNewCompany({...newCompany, nip: e.target.value})} className={ic} /></div>
                      <div><label className={lc}>{t('common.regon')}</label><input type="text" value={newCompany.regon} onChange={e => setNewCompany({...newCompany, regon: e.target.value})} className={ic} /></div>
                      <div><label className={lc}>{t('offers.contactPerson')}</label><input type="text" value={newCompany.contact_person} onChange={e => setNewCompany({...newCompany, contact_person: e.target.value})} className={ic} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className={lc}>{t('common.email')}</label><input type="email" value={newCompany.email} onChange={e => setNewCompany({...newCompany, email: e.target.value})} className={ic} /></div>
                      <div><label className={lc}>{t('common.phone')}</label><input type="text" value={newCompany.phone} onChange={e => setNewCompany({...newCompany, phone: e.target.value})} className={ic} /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><label className={lc}>{t('common.streetAndNumber')}</label><input type="text" value={newCompany.address_street} onChange={e => setNewCompany({...newCompany, address_street: e.target.value})} className={ic} /></div>
                      <div><label className={lc}>{t('common.zipCode')}</label><input type="text" value={newCompany.address_zip||''} onChange={e => setNewCompany({...newCompany, address_zip: e.target.value})} className={ic} /></div>
                      <div><label className={lc}>{t('common.city')}</label><input type="text" value={newCompany.address_city} onChange={e => setNewCompany({...newCompany, address_city: e.target.value})} className={ic} /></div>
                    </div>
                    <div><label className={lc}>{t('offers.montageAddress')}</label><input type="text" value={newCompany.address_montage||''} onChange={e => setNewCompany({...newCompany, address_montage: e.target.value})} className={ic} placeholder="Jeśli inny niż adres firmy" /></div>
                    <div><label className={lc}>{t('common.website')}</label><input type="text" value={newCompany.website||''} onChange={e => setNewCompany({...newCompany, website: e.target.value})} className={ic} /></div>
                  </>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setShowNewCompany(false)} className="text-xs text-gray-500">{t('common.cancel')}</button>
                  {newCompanyMode === 'manual' && (
                    <button type="button" onClick={handleAddCompany} disabled={!newCompany.name} className="px-3 py-1 bg-emerald-600 disabled:opacity-40 text-white rounded text-xs">{t('common.add')}</button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Adres montażu */}
      <div className="border-b pb-3">
        <label className={lc}>{t('offers.montageAddress')}</label>
        <input type="text" value={form.address_montage} onChange={e => setForm({...form, address_montage: e.target.value})} className={ic}
          placeholder="Adres miejsca realizacji (jeśli inny niż adres firmy)" />
      </div>
      {/* Osoba kontaktowa */}
      <div className="border-b pb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('offers.contactPerson')}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lc}>Imię i nazwisko</label><input type="text" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className={ic} /></div>
          <div><label className={lc}>Firma klienta</label><input type="text" value={form.client_company} onChange={e => setForm({...form, client_company: e.target.value})} className={ic} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div><label className={lc}>{t('common.email')}</label><input type="email" value={form.client_email} onChange={e => setForm({...form, client_email: e.target.value})} className={ic} /></div>
          <div><label className={lc}>{t('common.phone')}</label><input type="text" value={form.client_phone} onChange={e => setForm({...form, client_phone: e.target.value})} className={ic} /></div>
        </div>
      </div>
      {/* Terminy */}
      <div className="border-b pb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Terminy i waluta</h3>
        <div>
          <div className="mb-3">
            <label className={lc}>{t('offers.deadlineLabel')}</label>
            <div className="flex items-center gap-2">
              <select value={deadlineMode} onChange={e => {
                setDeadlineMode(e.target.value);
                if (e.target.value !== 'date') {
                  const days = e.target.value === 'days' ? (deadlineDays || 14) : (deadlineDays || 2) * 7;
                  const d = new Date(offer.created_at); d.setDate(d.getDate() + days);
                  setForm(f => ({...f, deadline: d.toISOString().split('T')[0]}));
                }
              }} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
                <option value="days">Dni</option>
                <option value="weeks">Tygodnie</option>
                <option value="date">Kalendarz</option>
              </select>
              {deadlineMode === 'days' && (
                <input type="number" min={1} value={deadlineDays} onChange={e => {
                  const d = parseInt(e.target.value) || 14;
                  setDeadlineDays(d);
                  const dt = new Date(offer.created_at); dt.setDate(dt.getDate() + d);
                  setForm(f => ({...f, deadline: dt.toISOString().split('T')[0]}));
                }} className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
              )}
              {deadlineMode === 'weeks' && (
                <input type="number" min={1} value={deadlineDays} onChange={e => {
                  const w = parseInt(e.target.value) || 2;
                  setDeadlineDays(w);
                  const dt = new Date(offer.created_at); dt.setDate(dt.getDate() + w * 7);
                  setForm(f => ({...f, deadline: dt.toISOString().split('T')[0]}));
                }} className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
              )}
              {deadlineMode === 'date' && (
                <input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
              )}
              <span className="text-xs text-gray-400">
                {form.deadline ? `do ${new Date(form.deadline).toLocaleDateString('pl-PL')}` : 'nie ustawiono'}
                {offer.created_at && <span className="ml-2 text-gray-300">(od {new Date(offer.created_at).toLocaleDateString('pl-PL')})</span>}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lc}>{t('common.currency')}</label>
              <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} className={ic}>
                <option value="PLN">PLN</option><option value="EUR">EUR</option><option value="USD">USD</option>
              </select></div>
            <div><label className={lc}>{t('offers.priceTypeLabel')}</label>
              <select value={form.price_type} onChange={e => setForm({...form, price_type: e.target.value})} className={ic}>
                <option value="netto">Netto</option><option value="brutto">Brutto</option>
              </select></div>
            <div><label className={lc}>{t('offers.vatRateLabel')}</label>
              <input type="number" value={form.vat_rate} onChange={e => setForm({...form, vat_rate: parseFloat(e.target.value) || 0})} className={ic} min="0" max="100" step="1" /></div>
          </div>
        </div>
        <div className="mt-3">
          <label className={lc}>{t('offers.validityLabel')}</label>
          <div className="flex items-center gap-2">
            <select value={validityMode} onChange={e => {
              setValidityMode(e.target.value);
              if (e.target.value !== 'date') {
                const days = e.target.value === 'days' ? (validityDays || 14) : (validityDays || 2) * 7;
                const d = new Date(offer.created_at); d.setDate(d.getDate() + days);
                setForm(f => ({...f, valid_until: d.toISOString().split('T')[0]}));
              }
            }} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
              <option value="days">Dni</option>
              <option value="weeks">Tygodnie</option>
              <option value="date">Kalendarz</option>
            </select>
            {validityMode === 'days' && (
              <input type="number" min={1} value={validityDays} onChange={e => {
                const d = parseInt(e.target.value) || 14;
                setValidityDays(d);
                const dt = new Date(offer.created_at); dt.setDate(dt.getDate() + d);
                setForm(f => ({...f, valid_until: dt.toISOString().split('T')[0]}));
              }} className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            )}
            {validityMode === 'weeks' && (
              <input type="number" min={1} value={validityDays} onChange={e => {
                const w = parseInt(e.target.value) || 2;
                setValidityDays(w);
                const dt = new Date(offer.created_at); dt.setDate(dt.getDate() + w * 7);
                setForm(f => ({...f, valid_until: dt.toISOString().split('T')[0]}));
              }} className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            )}
            {validityMode === 'date' && (
              <input type="date" value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            )}
            <span className="text-xs text-gray-400">
              {form.valid_until ? `do ${new Date(form.valid_until).toLocaleDateString('pl-PL')}` : 'nie ustawiono'}
              {offer.created_at && <span className="ml-2 text-gray-300">(od {new Date(offer.created_at).toLocaleDateString('pl-PL')})</span>}
            </span>
          </div>
        </div>
      </div>
      {/* Dodatkowe informacje + słownik */}
      <div>
        <label className={lc}>{t('offers.additionalInfo')}</label>
        <NotesDictionary
          selectedItems={form.extra_notes_items || []}
          onChange={v => setForm({...form, extra_notes_items: v})}
        />
      </div>
      {/* Współtwórcy */}
      {offer.id && <CollaboratorsSection offerId={offer.id} />}
      <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-1">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
        <button type="submit" className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg">{t('common.save')}</button>
      </div>
    </form>
  );
}

function CollaboratorsSection({ offerId }) {
  const { t } = useTranslation();
  const [collaborators, setCollaborators] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const [cr, ur] = await Promise.all([
        api.get(`/offers/${offerId}/collaborators`),
        api.get('/users')
      ]);
      setCollaborators(cr.data.collaborators || []);
      setUsers(ur.data.users?.filter(u => u.is_active && !u.is_archived) || []);
    } catch {}
  }, [offerId]);

  useEffect(() => { load(); }, [load]);

  const addCollab = async (userId) => {
    try {
      await api.post(`/offers/${offerId}/collaborators`, { user_id: userId });
      setSearch('');
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const removeCollab = async (userId) => {
    try {
      await api.delete(`/offers/${offerId}/collaborators/${userId}`);
      load();
    } catch { toast.error(t('common.error')); }
  };

  const collabIds = collaborators.map(c => c.user_id);
  const available = users.filter(u => !collabIds.includes(u.id) && u.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="border-t pt-3 mt-3">
      <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">{t('offers.collaboratorsSection')}</label>
      {collaborators.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {collaborators.map(c => (
            <span key={c.user_id} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 border border-purple-200 rounded-full text-xs text-purple-700">
              {c.short_name || c.name}
              <button type="button" onClick={() => removeCollab(c.user_id)} className="hover:text-red-500 ml-0.5">&times;</button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder={t('common.search') + '...'} />
      </div>
      {search && (
        <div className="border rounded-lg max-h-24 overflow-auto mt-1">
          {available.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">{t('common.noResults')}</div>
          ) : available.map(u => (
            <button key={u.id} type="button" onClick={() => addCollab(u.id)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 border-b last:border-0">
              <span className="font-medium">{u.name}</span>
              <span className="text-gray-400 ml-2">{u.short_name}</span>
              <span className="text-gray-400 ml-2">{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ACTION_LABELS = {
  offer_create: 'Utworzenie oferty',
  offer_update: 'Edycja oferty',
  offer_delete: 'Usunięcie oferty',
  offer_share: 'Udostępnienie linku',
  offer_message: 'Nowa wiadomość',
};

function OfferHistory({ offerId }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/offers/${offerId}/history`)
      .then(r => setHistory(r.data.history || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [offerId]);

  if (loading) return <div className="text-sm text-gray-400 py-4 text-center">{t('common.loading')}</div>;
  if (history.length === 0) return <div className="text-sm text-gray-400 py-4 text-center">{t('common.noResults')}</div>;

  return (
    <div className="space-y-1.5 max-h-[60vh] overflow-auto">
      {history.map(h => (
        <div key={h.id} className="flex items-start gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm">
          <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-800">{h.user_name || 'System'}</span>
              <span className="text-gray-400 text-xs">{new Date(h.created_at).toLocaleString('pl-PL')}</span>
            </div>
            <div className="text-gray-600">{ACTION_LABELS[h.action] || h.action}</div>
            {h.entity_name && <div className="text-xs text-gray-400">{h.entity_name}</div>}
            {h.details && Object.keys(h.details).length > 0 && (
              <div className="text-xs text-gray-400 mt-0.5">{JSON.stringify(h.details)}</div>
            )}
            {h.ip_address && <div className="text-[10px] text-gray-300">IP: {h.ip_address}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function VariantConversation({ offerId, variantId, variantName = '', token = null, clientMode = false }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [clientName, setClientName] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmWord, setConfirmWord] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const unreadCount = messages.filter(m => m.author_type === 'client' && !m.is_read).length;

  const loadMessages = useCallback(async () => {
    try {
      const url = token
        ? `/offers/public/${token}/variants/${variantId}/messages`
        : `/offers/${offerId}/variants/${variantId}/messages`;
      const r = await api.get(url);
      setMessages(r.data.messages || []);
    } catch {}
  }, [offerId, variantId, token]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Mark client messages as read when expanded (user side only)
  useEffect(() => {
    if (expanded && !token && unreadCount > 0) {
      api.put(`/offers/${offerId}/variants/${variantId}/messages/mark-read`).then(() => {
        setMessages(prev => prev.map(m => m.author_type === 'client' ? { ...m, is_read: true } : m));
      }).catch(() => {});
    }
  }, [expanded, offerId, variantId, token, unreadCount]);

  const handleSend = () => {
    if (!text.trim()) return;
    if (clientMode && !clientName.trim()) { toast.error('Podaj swoje imię'); return; }
    setShowConfirm(true);
    setConfirmWord('');
  };

  const confirmSend = async () => {
    if (confirmWord !== 'OK') { toast.error(t('offers.confirmTypeOk')); return; }
    try {
      const url = token
        ? `/offers/public/${token}/variants/${variantId}/messages`
        : `/offers/${offerId}/variants/${variantId}/messages`;
      if (attachment) {
        const formData = new FormData();
        formData.append('content', text);
        if (clientMode) formData.append('author_name', clientName);
        formData.append('attachment', attachment);
        await api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        const body = clientMode ? { content: text, author_name: clientName } : { content: text };
        await api.post(url, body);
      }
      setText('');
      setAttachment(null);
      setShowConfirm(false);
      setConfirmWord('');
      loadMessages();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  return (
    <div className="border-t border-gray-200 mx-2 mb-2">
      <div className="flex items-center justify-between py-1.5">
        <button onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600">
          <FileText size={12} />
          <span>{t('offers.conversation', { count: messages.length })}</span>
          {variantName && <span className="text-purple-400">{t('offers.regardingVariant', { name: variantName })}</span>}
          {unreadCount > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full leading-none">{t('offers.newMessage', { count: unreadCount })}</span>}
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        {expanded && messages.length > 1 && (
          <button onClick={() => setSortAsc(!sortAsc)} className="text-[10px] text-gray-400 hover:text-gray-600 px-1.5 py-0.5 border border-gray-200 rounded">
            {sortAsc ? t('common.fromOldest') : t('common.fromNewest')}
          </button>
        )}
      </div>
      {expanded && (() => {
        const messagesList = messages.length > 0 && (
          <div className="space-y-1.5">
            {[...messages].sort((a, b) => sortAsc ? new Date(a.created_at) - new Date(b.created_at) : new Date(b.created_at) - new Date(a.created_at)).map(m => (
              <div key={m.id} className={`rounded-lg px-3 py-2 text-xs ${m.author_type === 'client' ? (m.is_read === false && !token ? 'bg-amber-100 border-2 border-amber-400 ml-4' : 'bg-amber-50 border border-amber-200 ml-4') : 'bg-blue-50 border border-blue-200 mr-4'}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-semibold text-gray-700">
                    {m.author_name}
                    {m.author_type === 'client' && m.is_read === false && !token && <span className="ml-1.5 px-1 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded uppercase">nowa</span>}
                  </span>
                  <span className="text-gray-800 text-[10px] font-bold">{new Date(m.created_at).toLocaleString('pl-PL')}</span>
                </div>
                <div className="text-gray-600 whitespace-pre-wrap">{m.content}</div>
                {m.attachment_url && (
                  <button onClick={() => setLightbox(m.attachment_url)} className="block mt-1.5 cursor-zoom-in">
                    <img src={m.attachment_url} alt={m.attachment_name || 'Załącznik'} className="max-w-[200px] max-h-[150px] rounded border border-gray-200 hover:opacity-80" />
                  </button>
                )}
              </div>
            ))}
          </div>
        );
        const inputForm = !showConfirm ? (
            <div className="space-y-1.5">
              {clientMode && (
                <input type="text" value={clientName} onChange={e => setClientName(e.target.value)}
                  placeholder="Twoje imię i nazwisko" className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs" />
              )}
              <div className="flex gap-1.5">
                <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
                  placeholder={t('offers.writeMessage')} className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs resize-none" />
                <div className="flex flex-col gap-1 self-end">
                  <input type="file" accept="image/*" ref={fileInputRef} className="hidden"
                    onChange={e => { if (e.target.files[0]) setAttachment(e.target.files[0]); }} />
                  <button onClick={() => fileInputRef.current?.click()} type="button"
                    className="p-1.5 border border-gray-300 hover:bg-gray-100 rounded-lg text-gray-400" title={t('offers.attachPhoto')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                  </button>
                  <button onClick={handleSend} disabled={!text.trim()}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-xs">
                    {t('offers.sendMessage')}
                  </button>
                </div>
              </div>
              {attachment && (
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded px-2 py-1">
                  <img src={URL.createObjectURL(attachment)} alt="" className="w-10 h-10 object-cover rounded" />
                  <span className="truncate">{attachment.name}</span>
                  <button onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-red-400 hover:text-red-600 ml-auto">&times;</button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
              <div className="text-xs text-amber-800">
                {t('offers.confirmWarning')} {t('offers.confirmTypeOk')}
              </div>
              <div className="bg-white border border-gray-200 rounded px-2.5 py-1.5 text-xs text-gray-600 whitespace-pre-wrap mb-1">
                {text}
                {attachment && <img src={URL.createObjectURL(attachment)} alt="" className="mt-1.5 max-w-[150px] max-h-[100px] rounded border" />}
              </div>
              <div className="flex gap-2 items-center">
                <input type="text" value={confirmWord} onChange={e => setConfirmWord(e.target.value)}
                  placeholder="Wpisz OK" className="w-20 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-bold text-center"
                  onKeyDown={e => { if (e.key === 'Enter') confirmSend(); }} autoFocus />
                <button onClick={confirmSend} disabled={confirmWord !== 'OK'}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded-lg text-xs">
                  {t('common.confirm')}
                </button>
                <button onClick={() => { setShowConfirm(false); setConfirmWord(''); }}
                  className="px-3 py-1.5 text-gray-500 hover:bg-gray-100 rounded-lg text-xs">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          );
        return (
          <div className="pb-2 space-y-2">
            {sortAsc ? <>{messagesList}{inputForm}</> : <>{inputForm}{messagesList}</>}
          </div>
        );
      })()}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function ShareOfferDialog({ offer, onClose }) {
  const { t } = useTranslation();
  const [shares, setShares] = useState([]);
  const [expiresDays, setExpiresDays] = useState(30);
  const [newLink, setNewLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const loadShares = useCallback(async () => {
    try { const r = await api.get(`/offers/${offer.id}/shares`); setShares(r.data.shares || []); }
    catch {}
  }, [offer.id]);

  useEffect(() => { loadShares(); }, [loadShares]);

  const createLink = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/offers/${offer.id}/share`, { expires_days: expiresDays });
      const url = `${window.location.origin}/offer/${r.data.share.token}`;
      setNewLink(url);
      loadShares();
      toast.success(t('common.created'));
    } catch { toast.error(t('common.error')); }
    finally { setLoading(false); }
  };

  const deactivate = async (id) => {
    try { await api.delete(`/offers/${offer.id}/shares/${id}`); loadShares(); setNewLink(null); }
    catch { toast.error(t('common.error')); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success(t('common.saved')));
  };

  const company = offer.company_name || offer.client_company || '';
  const issuer = offer.issuer_name || '';
  const emailSubject = `Oferta ${offer.offer_number} - ${offer.name}`;
  const { user: sharingUser } = useAuth();
  const emailBody = newLink ? [
    `Szanowni Państwo,`,
    ``,
    `Przesyłam ofertę ${offer.offer_number} "${offer.name}"${company ? ` dla ${company}` : ''}.`,
    ``,
    `Oferta dostępna pod linkiem:`,
    newLink,
    ``,
    `Pod powyższym linkiem mogą Państwo również prowadzić konwersację dotyczącą oferty.`,
    ``,
    offer.valid_until ? `Oferta ważna do: ${new Date(offer.valid_until).toLocaleDateString('pl-PL')}` : '',
    ``,
    `Z poważaniem,`,
    sharingUser?.name || '',
    sharingUser?.job_title || '',
    sharingUser?.contact_email || sharingUser?.email || '',
    sharingUser?.phone_mobile ? `kom. ${sharingUser.phone_mobile}` : '',
    sharingUser?.phone_office ? `tel. ${sharingUser.phone_office}` : '',
    issuer,
  ].filter(l => l !== undefined && l !== '').join('\n') : '';

  return (
    <div className="space-y-4">
      {!newLink ? (
        <div>
          <p className="text-sm text-gray-500 mb-3">Wygeneruj link, pod którym klient zobaczy ofertę bez logowania.</p>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600">Wygasa po</label>
            <select value={expiresDays} onChange={e => setExpiresDays(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
              <option value={7}>7 dni</option>
              <option value={14}>14 dni</option>
              <option value={30}>30 dni</option>
              <option value={60}>60 dni</option>
              <option value={90}>90 dni</option>
            </select>
            <button onClick={createLink} disabled={loading}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-40">
              <Link2 size={14} /> {t('offers.generateLink')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('offers.linkToOffer')}</label>
            <div className="flex gap-2">
              <input type="text" value={newLink} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono text-gray-700" />
              <button onClick={() => copyToClipboard(newLink)} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-sm">{t('offers.copyLink')}</button>
              <a href={newLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"><Eye size={14} /> {t('offers.openLink')}</a>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Gotowa wiadomość email</label>
            <textarea value={emailBody} readOnly rows={10} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700" />
            <div className="flex gap-2 mt-2">
              <button onClick={() => copyToClipboard(emailBody)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-xs">Kopiuj treść</button>
              <a href={`mailto:${offer.company_email || offer.client_email || ''}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium">
                <Mail size={12} /> Otwórz w kliencie email
              </a>
            </div>
          </div>
          <button onClick={() => setNewLink(null)} className="text-xs text-gray-400 hover:text-gray-600">Generuj kolejny link</button>
        </div>
      )}

      {/* Active shares */}
      {shares.filter(s => s.is_active).length > 0 && (
        <div className="border-t pt-3">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('offers.activeLinks')}</div>
          <div className="space-y-1.5">
            {shares.filter(s => s.is_active).map(s => (
              <div key={s.id} className="bg-gray-50 rounded-lg px-3 py-2.5 text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-700">{s.creator_name}</span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => copyToClipboard(`${window.location.origin}/offer/${s.token}`)} className="px-2 py-0.5 text-gray-500 hover:bg-gray-200 rounded">{t('offers.copyLink')}</button>
                    <a href={`/offer/${s.token}`} target="_blank" rel="noopener noreferrer" className="px-2 py-0.5 text-purple-500 hover:bg-purple-50 rounded">{t('offers.openLink')}</a>
                    <button onClick={() => deactivate(s.id)} className="px-2 py-0.5 text-red-500 hover:bg-red-50 rounded">{t('offers.deactivate')}</button>
                  </div>
                </div>
                <div className="text-gray-400">
                  Od: {new Date(s.created_at).toLocaleString('pl-PL')}
                  {s.expires_at ? ` — do: ${new Date(s.expires_at).toLocaleDateString('pl-PL')}` : ' — bezterminowo'}
                </div>
                <div className="text-gray-400">
                  {s.view_count > 0
                    ? <><span className="text-blue-500">{s.view_count}x kliknięto</span>{s.last_viewed_at && <span> — ostatnio: {new Date(s.last_viewed_at).toLocaleString('pl-PL')}</span>}</>
                    : <span>Brak kliknięć</span>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotesDictionary({ selectedItems, onChange }) {
  const { t } = useTranslation();
  // selectedItems: [{id, title, content}, ...] — ordered list of selected notes
  const [dictItems, setDictItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [customText, setCustomText] = useState('');
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  useEffect(() => {
    api.get('/offers/notes-dictionary').then(r => setDictItems(r.data.items)).catch(() => {});
  }, []);

  const items = selectedItems || [];

  const isSelected = (dictId) => items.some(i => i.id === dictId);

  const toggleItem = (dictItem) => {
    if (isSelected(dictItem.id)) {
      onChange(items.filter(i => i.id !== dictItem.id));
    } else {
      onChange([...items, { id: dictItem.id, title: dictItem.title, content: dictItem.content }]);
    }
  };

  const addCustom = () => {
    if (!customText.trim()) return;
    onChange([...items, { id: null, title: 'Własna uwaga', content: customText.trim() }]);
    setCustomText('');
  };

  const removeItem = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  // D&D reorder
  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOver.current = idx; };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) {
      dragItem.current = null; dragOver.current = null; return;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOver.current, 0, moved);
    onChange(reordered);
    dragItem.current = null; dragOver.current = null;
  };

  const handleAddDict = async () => {
    if (!newTitle || !newContent) return;
    try {
      const r = await api.post('/offers/notes-dictionary', { title: newTitle, content: newContent });
      setDictItems(prev => [...prev, r.data.item]);
      setShowAdd(false); setNewTitle(''); setNewContent('');
    } catch {}
  };

  const handleDeleteDict = async (id) => {
    try { await api.delete(`/offers/notes-dictionary/${id}`); setDictItems(prev => prev.filter(i => i.id !== id)); } catch {}
  };

  return (
    <div className="space-y-3">
      {/* Dictionary — checkboxes */}
      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">Słownik (zaznacz aby dodać do oferty)</div>
        <div className="bg-gray-50 border rounded-lg p-2 space-y-0.5 max-h-36 overflow-auto">
          {dictItems.map(d => (
            <label key={d.id} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-white rounded p-1 group">
              <input type="checkbox" checked={isSelected(d.id)} onChange={() => toggleItem(d)} className="mt-0.5 rounded border-gray-300" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-700">{d.title}</span>
                <span className="text-gray-400 ml-1">{d.content.length > 50 ? d.content.substring(0, 50) + '...' : d.content}</span>
              </div>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteDict(d.id); }}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-0.5 flex-shrink-0"><Trash2 size={10} /></button>
            </label>
          ))}
          {dictItems.length === 0 && <div className="text-xs text-gray-400 py-2 text-center">Słownik pusty</div>}
        </div>
        <div className="flex gap-2 mt-1">
          {showAdd ? (
            <div className="flex-1 bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
              <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs" placeholder="Tytuł" autoFocus />
              <textarea value={newContent} onChange={e => setNewContent(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs" rows={2} placeholder="Treść..." />
              <div className="flex justify-end gap-1">
                <button type="button" onClick={() => setShowAdd(false)} className="text-xs text-gray-500">{t('common.cancel')}</button>
                <button type="button" onClick={handleAddDict} disabled={!newTitle || !newContent} className="px-2 py-0.5 bg-blue-600 disabled:opacity-40 text-white rounded text-xs">{t('common.add')}</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setShowAdd(true)} className="text-xs text-blue-600 hover:underline">+ Dodaj do słownika</button>
          )}
        </div>
      </div>

      {/* Selected items with D&D reorder */}
      {items.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Wybrane informacje (kolejność = kolejność w PDF, przeciągnij aby zmienić)</div>
          <div className="border rounded-lg divide-y">
            {items.map((item, idx) => (
              <div key={idx} draggable onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)} onDragEnd={handleDragEnd} onDragOver={e => e.preventDefault()}
                className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 cursor-grab select-none group">
                <GripVertical size={12} className="text-gray-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <input type="text" value={item.title}
                    onChange={e => { const updated = [...items]; updated[idx] = {...updated[idx], title: e.target.value}; onChange(updated); }}
                    className="text-xs font-medium text-gray-700 w-full border-0 bg-transparent p-0 focus:ring-0 focus:outline-none" />
                  <textarea value={item.content}
                    onChange={e => { const updated = [...items]; updated[idx] = {...updated[idx], content: e.target.value}; onChange(updated); }}
                    className="text-xs text-gray-500 mt-0.5 w-full border-0 bg-transparent p-0 focus:ring-0 focus:outline-none resize-none"
                    rows={Math.max(1, Math.ceil(item.content.length / 60))} />
                </div>
                <button type="button" onClick={() => removeItem(idx)}
                  className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-0.5 flex-shrink-0 mt-0.5"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom text */}
      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">Własna uwaga (jednorazowa)</div>
        <div className="flex gap-2">
          <textarea value={customText} onChange={e => setCustomText(e.target.value)}
            className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs" rows={2}
            placeholder="Wpisz tekst i dodaj do listy..." />
          <button type="button" onClick={addCustom} disabled={!customText.trim()}
            className="px-3 py-1 bg-gray-600 disabled:opacity-40 text-white rounded text-xs self-end">{t('common.add')}</button>
        </div>
      </div>
    </div>
  );
}

function EditableField({ value, placeholder, onSave }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);

  if (editing) {
    return (
      <input type="text" value={text} onChange={e => setText(e.target.value)} autoFocus
        className="text-xs border-b border-purple-400 outline-none bg-transparent px-0 min-w-[80px]"
        placeholder={placeholder}
        onBlur={() => { if (text !== value) onSave(text); setEditing(false); }}
        onKeyDown={e => { if (e.key === 'Enter') { if (text !== value) onSave(text); setEditing(false); } if (e.key === 'Escape') { setText(value); setEditing(false); } }} />
    );
  }
  return (
    <span className="text-xs cursor-pointer hover:text-purple-600 truncate" onClick={() => setEditing(true)} title="Kliknij aby edytować">
      {value || <span className="text-gray-300 italic">{placeholder}</span>}
    </span>
  );
}

function EditableTitle({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);

  if (editing) {
    return (
      <input type="text" value={text} onChange={e => setText(e.target.value)} autoFocus
        className="text-lg font-bold text-gray-800 border-b-2 border-purple-400 outline-none bg-transparent px-0"
        onBlur={() => { if (text.trim() && text !== value) onSave(text.trim()); setEditing(false); }}
        onKeyDown={e => { if (e.key === 'Enter') { if (text.trim() && text !== value) onSave(text.trim()); setEditing(false); } if (e.key === 'Escape') { setText(value); setEditing(false); } }} />
    );
  }
  return (
    <h1 className="text-lg font-bold text-gray-800 cursor-pointer hover:text-purple-600 transition" onClick={() => setEditing(true)} title="Kliknij aby zmienić nazwę">
      {value}
    </h1>
  );
}

function ItemForm({ initial, isGroup, onSave, onCancel }) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ name, description }); }} className="space-y-4">
      <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required autoFocus /></div>
      <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
        <button type="submit" className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg">{initial ? t('common.save') : t('common.add')}</button>
      </div>
    </form>
  );
}

// ==================== MAIN ====================

export default function OffersPage() {
  const { t } = useTranslation();
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const toast = useToast();
  const [selectedOffer, _setSelectedOffer] = useState(() => {
    // Check URL for ?open=ID
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (openId) {
      window.history.replaceState({}, '', window.location.pathname);
      return { id: parseInt(openId) };
    }
    const saved = localStorage.getItem('swibi_offer_selected');
    if (saved) { try { return JSON.parse(saved); } catch {} }
    return null;
  });
  const setSelectedOffer = (offer) => {
    _setSelectedOffer(offer);
    if (offer) localStorage.setItem('swibi_offer_selected', JSON.stringify({ id: offer.id, name: offer.name, offer_number: offer.offer_number }));
    else localStorage.removeItem('swibi_offer_selected');
  };

  if (!canView('offers')) {
    return <div className="p-8 text-center text-gray-400"><FileText size={48} className="mx-auto mb-4 opacity-50" /><p>{t('common.noAccess')}</p></div>;
  }

  const [listActionOffer, setListActionOffer] = useState(null);
  const [listActionType, setListActionType] = useState(null);
  const { user: currentUser } = useAuth();

  const handleListAction = (offer, action) => {
    setListActionOffer(offer);
    setListActionType(action);
  };

  return (
    <div className={selectedOffer ? "p-4 h-screen flex flex-col overflow-hidden" : "p-6"}>
      {selectedOffer ? (
        <OfferDetail offer={selectedOffer} onBack={() => setSelectedOffer(null)}
          canCreate={canCreate('offers')} canEdit={canEdit('offers')} canDelete={canDelete('offers')} toast={toast} />
      ) : (<>
        <OffersList onSelect={setSelectedOffer} canCreate={canCreate('offers')} canDelete={canDelete('offers')} toast={toast} onAction={handleListAction} />
        {listActionOffer && listActionType === 'details' && (
          <Modal isOpen={true} onClose={() => setListActionOffer(null)} title={t('offers.detailsButton')} maxWidth="max-w-2xl">
            <OfferDetailsForm offer={listActionOffer} onSave={async (data) => {
              try { await api.put(`/offers/${listActionOffer.id}`, data); toast.success(t('common.saved')); setListActionOffer(null); }
              catch { toast.error(t('common.error')); }
            }} onCancel={() => setListActionOffer(null)} />
          </Modal>
        )}
        {listActionOffer && listActionType === 'share' && (
          <Modal isOpen={true} onClose={() => setListActionOffer(null)} title={t('offers.shareOfferTitle')} maxWidth="max-w-lg">
            <ShareOfferDialog offer={listActionOffer} onClose={() => setListActionOffer(null)} />
          </Modal>
        )}
        {listActionOffer && listActionType === 'pdf' && (
          <Modal isOpen={true} onClose={() => setListActionOffer(null)} title={t('offers.pdfButton')}>
            <div className="text-sm text-gray-500 text-center py-4">Otwórz ofertę aby wygenerować PDF</div>
          </Modal>
        )}
      </>)}
    </div>
  );
}
